import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip,
  Button, IconButton, Alert, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, TextField,
  Tooltip, Menu, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  Switch, Collapse
} from '@mui/material';
import {
  History as HistoryIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon,
  EditNote as EditNoteIcon,
  AccessTime as AccessTimeIcon,
  ShowChart as ShowChartIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  BarChart as BarChartIcon,
  DeleteForever as DeleteForeverIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  AccountTree as AccountTreeIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  PlayArrow as PlayArrowIcon,
  NotInterested as NotInterestedIcon
} from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import TradeHistoryService from '../services/TradeHistoryService';
import StrategyEngineService from '../services/StrategyEngineService';
import MarketSessionsMonitor from '../components/widgets/MarketSessionsMonitor';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// TradingView symbol mapping for rebranded tokens
const getTradingViewSymbol = (symbol, exchange = 'KUCOIN') => {
  const normalized = symbol?.replace('-', '').replace('/', '').toUpperCase() || 'BTCUSDT';
  const symbolMap = {
    'MATICUSDT': 'POLUSDT',
    'MATIC': 'POL',
    'LUNAUSDT': 'LUNCUSDT',
    'LUNA': 'LUNC',
    'USTUSDT': 'USTCUSDT',
    'UST': 'USTC',
  };
  const mappedSymbol = symbolMap[normalized] || normalized;
  const finalSymbol = mappedSymbol.endsWith('USDT') ? mappedSymbol : `${mappedSymbol}USDT`;
  const exchangeMap = { 'BINANCE': 'BINANCE', 'KUCOIN': 'KUCOIN', 'COINBASE': 'COINBASE', 'KRAKEN': 'KRAKEN', 'BYBIT': 'BYBIT' };
  return `${exchangeMap[exchange.toUpperCase()] || 'KUCOIN'}:${finalSymbol}`;
};

// Format confidence for display — confidence is stored as 0-100 (e.g. 98.00)
const formatConfidence = (conf) => {
  if (!conf && conf !== 0) return null;
  const val = Number(conf);
  // If > 1, it's already a percentage (98.00); if <= 1, it's a fraction (0.98)
  return val > 1 ? val.toFixed(0) : (val * 100).toFixed(0);
};

// v4.4.3: Signal countdown timer — shows remaining validity time
const SignalCountdown = ({ expiresAt }) => {
  const [remaining, setRemaining] = React.useState('');
  const [expired, setExpired] = React.useState(false);
  
  React.useEffect(() => {
    const update = () => {
      const now = Date.now();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;
      if (diff <= 0) { setExpired(true); setRemaining('Expired'); return; }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  return (
    <Typography variant="caption" display="block" sx={{ 
      fontSize: '0.6rem', mt: 0.2,
      color: expired ? 'error.main' : remaining.startsWith('0:') || remaining.startsWith('1:') ? 'error.main' : 'text.secondary',
      fontFamily: 'monospace'
    }}>
      {expired ? '⏰ Expired' : `⏱ ${remaining}`}
    </Typography>
  );
};

// Strategy running timer — shows elapsed time since activation, stops when deactivated
const StrategyRunTimer = ({ activatedAt, isRunning }) => {
  const [elapsed, setElapsed] = React.useState('');
  
  React.useEffect(() => {
    if (!activatedAt) { setElapsed('—'); return; }
    const startTs = new Date(activatedAt).getTime();
    if (isNaN(startTs)) { setElapsed('—'); return; }
    
    const update = () => {
      const diff = Date.now() - startTs;
      if (diff < 0) { setElapsed('0s'); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      if (days > 0) setElapsed(`${days}d ${hours}h ${mins}m`);
      else if (hours > 0) setElapsed(`${hours}h ${mins}m ${secs}s`);
      else setElapsed(`${mins}m ${secs}s`);
    };
    update();
    if (!isRunning) return; // Don't tick when stopped
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activatedAt, isRunning]);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      <AccessTimeIcon sx={{ fontSize: 11, color: isRunning ? '#00FF94' : '#6B7280' }} />
      <Typography sx={{
        fontSize: '0.6rem', fontFamily: '"JetBrains Mono", monospace',
        color: isRunning ? '#00FF94' : '#6B7280',
      }}>
        {elapsed}
      </Typography>
    </Box>
  );
};

function TradeHistory() {
  const [tradeData, setTradeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [activeStrategies, setActiveStrategies] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [closingAllPositions, setClosingAllPositions] = useState(false);
  const [confirmCloseAllOpen, setConfirmCloseAllOpen] = useState(false);
  const [lossReportOpen, setLossReportOpen] = useState(false);
  
  // Signal execution state
  const [executingSignal, setExecutingSignal] = useState(null);
  const [bulkSignalExecuting, setBulkSignalExecuting] = useState(false);
  const [bulkSignalDismissing, setBulkSignalDismissing] = useState(false);
  
  // Bulk close state
  const [bulkCloseConfirmOpen, setBulkCloseConfirmOpen] = useState(false);
  const [bulkCloseFilterType, setBulkCloseFilterType] = useState(null); // 'PROFIT_ONLY'|'LOSS_ONLY'|'PROFIT_AT_LEAST'|'LOSS_AT_MOST'
  const [bulkCloseThreshold, setBulkCloseThreshold] = useState(5);
  const [bulkCloseExecuting, setBulkCloseExecuting] = useState(false);
  const [bulkClosePreview, setBulkClosePreview] = useState(null);
  const [bulkCloseResults, setBulkCloseResults] = useState(null);
  const [bulkCloseResultsOpen, setBulkCloseResultsOpen] = useState(false);
  const [strategyBreakdownOpen, setStrategyBreakdownOpen] = useState(() => {
    return localStorage.getItem('strategyBreakdownOpen') !== 'false';
  });
  
  // Strategy breakdown sorting: 'profitable' | 'oldest' | 'newest'
  const [strategySortMode, setStrategySortMode] = useState(() => {
    return localStorage.getItem('strategySortMode') || 'profitable';
  });
  
  // Execution status for strategy toggle switches (synced with Strategy Builder)
  const [executionStatus, setExecutionStatus] = useState({ strategies: [], conflicts: [] });
  
  const loadExecutionStatus = useCallback(async () => {
    try {
      const status = await StrategyEngineService.getExecutionStatus();
      setExecutionStatus(status);
    } catch (error) {
      console.error("Error loading execution status:", error);
    }
  }, []);
  
  useEffect(() => {
    loadExecutionStatus();
    const interval = setInterval(loadExecutionStatus, 30000);
    return () => clearInterval(interval);
  }, [loadExecutionStatus]);
  
  const toggleStrategySchedule = async (strategyId, e) => {
    if (e) e.stopPropagation();
    // Optimistic UI update
    setExecutionStatus(prev => ({
      ...prev,
      strategies: (prev.strategies || []).map(s =>
        s.strategyId === strategyId ? { ...s, enabled: !s.enabled } : s
      ),
    }));
    try {
      await StrategyEngineService.toggleSchedule(strategyId);
      await loadExecutionStatus();
    } catch (error) {
      console.error("Error toggling strategy:", error);
      await loadExecutionStatus();
    }
  };
  
  const handleTradingModeToggle = async (strategyId, field, value) => {
    // Optimistic UI update
    setExecutionStatus(prev => ({
      ...prev,
      strategies: (prev.strategies || []).map(s =>
        s.strategyId === strategyId ? { ...s, [field]: value } : s
      ),
    }));
    try {
      await StrategyEngineService.toggleTradingMode(strategyId, field, value);
      await loadExecutionStatus();
    } catch (error) {
      console.error("Error toggling trading mode:", error);
      await loadExecutionStatus();
    }
  };

  // Mother/child group expand/collapse state
  const [expandedGroups, setExpandedGroups] = useState({});
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };
  
  // Show only open positions toggle
  const [showOpenOnly, setShowOpenOnly] = useState(() => {
    return localStorage.getItem('tradeHistoryShowOpenOnly') === 'true';
  });
  
  // Global TP/SL/AI toggle state for all open trades
  const [globalTpEnabled, setGlobalTpEnabled] = useState(true);
  const [globalSlEnabled, setGlobalSlEnabled] = useState(true);
  const [globalAiExitEnabled, setGlobalAiExitEnabled] = useState(true);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Compute global TP/SL state from open trades
  useEffect(() => {
    if (tradeData?.trades) {
      const openTrades = tradeData.trades.filter(t => t.status === 'open' || t.status === 'pending');
      if (openTrades.length > 0) {
        const allTpOn = openTrades.every(t => t.autoTpEnabled !== false);
        const allSlOn = openTrades.every(t => t.autoSlEnabled !== false);
        const allAiOn = openTrades.every(t => t.autoAiExitEnabled !== false);
        setGlobalTpEnabled(allTpOn);
        setGlobalSlEnabled(allSlOn);
        setGlobalAiExitEnabled(allAiOn);
      }
    }
  }, [tradeData?.trades]);
  
  // Handler: toggle TP/SL for ALL open positions
  const handleBulkTpSlToggle = async (field, value) => {
    setBulkUpdating(true);
    try {
      // v3.9.18: Include strategyId so backend persists global toggles to schedule for future positions
      const openTrades = tradeData?.trades?.filter(t => t.status === 'open' || t.status === 'pending') || [];
      const strategyId = openTrades[0]?.strategyId || null;
      await StrategyEngineService.bulkUpdatePositionSettings({ [field]: value, ...(strategyId && { strategyId }) });
      // Update local state
      setTradeData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          trades: prev.trades.map(t => {
            if (t.status === 'open' || t.status === 'pending') {
              return { ...t, [field]: value };
            }
            return t;
          })
        };
      });
      if (field === 'autoTpEnabled') setGlobalTpEnabled(value);
      if (field === 'autoSlEnabled') setGlobalSlEnabled(value);
      if (field === 'autoAiExitEnabled') setGlobalAiExitEnabled(value);
    } catch (err) {
      console.error('Bulk TP/SL update failed:', err);
    } finally {
      setBulkUpdating(false);
    }
  };
  
  // Manual budget add state
  const [addingBudget, setAddingBudget] = useState(null); // positionId being added to
  
  // Handler: manual budget add for a position
  const handleManualBudgetAdd = async (trade) => {
    if (addingBudget) return; // prevent double-click
    setAddingBudget(trade.id);
    try {
      const result = await StrategyEngineService.manualBudgetAdd(trade.runId, trade.id);
      if (result?.data?.position) {
        const updatedPos = result.data.position;
        setTradeData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            trades: prev.trades.map(t => t.id === trade.id ? { ...t, ...updatedPos } : t)
          };
        });
        if (selectedTrade?.id === trade.id) {
          setSelectedTrade(prev => ({ ...prev, ...updatedPos }));
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to add budget';
      setError(msg);
      console.error('Manual budget add failed:', err);
    } finally {
      setAddingBudget(null);
    }
  };
  
  // Handler: toggle TP/SL for a single position (inline row)
  const handleRowTpSlToggle = async (trade, field, value) => {
    try {
      await StrategyEngineService.updatePositionSettings(trade.runId, trade.id, { [field]: value });
      setTradeData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          trades: prev.trades.map(t => t.id === trade.id ? { ...t, [field]: value } : t)
        };
      });
      // Also update selectedTrade if it's the same position
      if (selectedTrade?.id === trade.id) {
        setSelectedTrade(prev => ({ ...prev, [field]: value }));
      }
    } catch (err) {
      console.error('Row TP/SL update failed:', err);
    }
  };
  
  // Save showOpenOnly to localStorage
  useEffect(() => {
    localStorage.setItem('tradeHistoryShowOpenOnly', showOpenOnly.toString());
  }, [showOpenOnly]);
  
  // Group by Strategy toggle
  const [groupByStrategy, setGroupByStrategy] = useState(() => {
    return localStorage.getItem('tradeHistoryGroupByStrategy') === 'true';
  });
  const [activeStrategyTab, setActiveStrategyTab] = useState(0);
  
  useEffect(() => {
    localStorage.setItem('tradeHistoryGroupByStrategy', groupByStrategy.toString());
  }, [groupByStrategy]);
  
  // Trading mode: 'paper' or 'real'
  const [tradingMode, setTradingMode] = useState(() => {
    return localStorage.getItem('tradeHistoryMode') || 'paper';
  });
  
  // Save trading mode to localStorage
  useEffect(() => {
    localStorage.setItem('tradeHistoryMode', tradingMode);
  }, [tradingMode]);
  
  // Sound notification for new trades
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('tradeHistorySoundEnabled') !== 'false';
  });
  const prevTradeCountRef = useRef(-1);
  
  useEffect(() => {
    localStorage.setItem('tradeHistorySoundEnabled', soundEnabled.toString());
  }, [soundEnabled]);
  
  const playNewTradeSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      // Rising tone: new trade opened
      osc.frequency.setValueAtTime(660, ctx.currentTime);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) { /* Audio not available */ }
  }, [soundEnabled]);

  // Profit close sound: custom MP3
  const playProfitCloseSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio('/sounds/profit-close.mp3');
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch (e) { /* Audio not available */ }
  }, [soundEnabled]);

  // Loss close sound: custom MP3
  const playLossCloseSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio('/sounds/loss-close.mp3');
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch (e) { /* Audio not available */ }
  }, [soundEnabled]);

  // Pinned trades (max 5)
  const [pinnedTradeIds, setPinnedTradeIds] = useState(() => {
    const saved = localStorage.getItem('pinnedTradeIds');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Save pinned trades to localStorage
  useEffect(() => {
    localStorage.setItem('pinnedTradeIds', JSON.stringify(pinnedTradeIds));
  }, [pinnedTradeIds]);
  
  // Toggle pin for a trade
  const togglePinTrade = (tradeId) => {
    setPinnedTradeIds(prev => {
      if (prev.includes(tradeId)) {
        // Unpin
        return prev.filter(id => id !== tradeId);
      } else {
        // Filter out any stale pinned IDs that no longer exist in current trades
        const currentTradeIds = tradeData?.trades?.map(t => t.id) || [];
        const validPinnedIds = prev.filter(id => currentTradeIds.includes(id));
        
        // Pin (max 5)
        if (validPinnedIds.length >= 5) {
          alert('Maximum 5 trades can be pinned. Unpin one first.');
          return validPinnedIds;
        }
        return [...validPinnedIds, tradeId];
      }
    });
  };
  
  // Clean up stale pinned IDs when trade data changes
  useEffect(() => {
    if (tradeData?.trades) {
      const currentTradeIds = tradeData.trades.map(t => t.id);
      setPinnedTradeIds(prev => {
        const validIds = prev.filter(id => currentTradeIds.includes(id));
        if (validIds.length !== prev.length) {
          return validIds;
        }
        return prev;
      });
    }
  }, [tradeData?.trades]);
  
  // Close all open positions
  const handleCloseAllPositions = async () => {
    setClosingAllPositions(true);
    try {
      const API_BASE = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';
      const response = await fetch(`${API_BASE}/api/runs/positions/close-all`, {
        method: 'POST',
      });
      const result = await response.json();
      
      if (result.success) {
        setConfirmCloseAllOpen(false);
        alert(`✅ ${result.message}`);
        fetchTradeHistory(); // Refresh to show closed positions
      } else {
        throw new Error(result.error || 'Failed to close positions');
      }
    } catch (err) {
      console.error('Error closing all positions:', err);
      alert(`❌ Failed to close positions: ${err.message}`);
    } finally {
      setClosingAllPositions(false);
    }
  };
  
  // Compute eligible counts for bulk close buttons (client-side estimation)
  const openTrades = tradeData?.trades?.filter(t => t.status === 'open') || [];
  const bulkCloseCounts = {
    PROFIT_ONLY: openTrades.filter(t => parseFloat(t.pnlPercent || 0) > 0).length,
    LOSS_ONLY: openTrades.filter(t => parseFloat(t.pnlPercent || 0) < 0).length,
    PROFIT_AT_LEAST: openTrades.filter(t => parseFloat(t.pnlPercent || 0) >= bulkCloseThreshold).length,
    LOSS_AT_MOST: openTrades.filter(t => parseFloat(t.pnlPercent || 0) <= -bulkCloseThreshold).length,
  };

  // Open bulk close confirm dialog with dry-run preview
  const handleBulkCloseOpen = async (filterType) => {
    setBulkCloseFilterType(filterType);
    setBulkClosePreview(null);
    setBulkCloseResults(null);
    setBulkCloseConfirmOpen(true);
    try {
      const filter = { type: filterType };
      if (filterType === 'PROFIT_AT_LEAST' || filterType === 'LOSS_AT_MOST') {
        filter.thresholdPct = bulkCloseThreshold;
      }
      const preview = await StrategyEngineService.bulkClosePositions(filter, true);
      setBulkClosePreview(preview);
    } catch (err) {
      console.error('Bulk close preview failed:', err);
    }
  };

  // Execute bulk close
  const handleBulkCloseExecute = async () => {
    setBulkCloseExecuting(true);
    try {
      const filter = { type: bulkCloseFilterType };
      if (bulkCloseFilterType === 'PROFIT_AT_LEAST' || bulkCloseFilterType === 'LOSS_AT_MOST') {
        filter.thresholdPct = bulkCloseThreshold;
      }
      const result = await StrategyEngineService.bulkClosePositions(filter, false);
      setBulkCloseConfirmOpen(false);
      setBulkCloseResults(result);
      setBulkCloseResultsOpen(true);
      fetchTradeHistory();
    } catch (err) {
      console.error('Bulk close failed:', err);
      alert(`❌ Bulk close failed: ${err.message}`);
    } finally {
      setBulkCloseExecuting(false);
    }
  };

  // Clear trade history for current trading mode (paper or real)
  const handleClearHistory = async () => {
    setClearingHistory(true);
    try {
      const API_BASE = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';
      const response = await fetch(`${API_BASE}/api/runs/positions/clear-all?mode=${tradingMode}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      
      if (result.success) {
        // Remove only trades matching the cleared mode from local state
        setTradeData(prev => {
          if (!prev) return prev;
          const remainingTrades = prev.trades.filter(trade => {
            const isPaperTrade = trade.paperTrading === true || trade.isSimulated === true || !trade.realMoney;
            return tradingMode === 'paper' ? !isPaperTrade : isPaperTrade;
          });
          if (remainingTrades.length === 0) {
            return {
              trades: [],
              summary: {
                totalTrades: 0,
                openTrades: 0,
                activeStrategies: 0,
                bestPerforming: { strategy: 'N/A', return: '0.00%' },
                returns: { daily: '0.00%', weekly: '0.00%', monthly: '0.00%', yearly: '0.00%' },
                distribution: { crypto: '100%', stocks: '0%', forex: '0%', futures: '0%' }
              },
              performance: {
                dailyData: [],
                metrics: {
                  sharpeRatio: '0.00',
                  maxDrawdown: '0.00%',
                  profitFactor: '0.00',
                  winRate: '0.00%',
                  avgWin: '0.00%',
                  avgLoss: '0.00%',
                  expectancy: '0.00%'
                }
              }
            };
          }
          return { ...prev, trades: remainingTrades };
        });
        // Clear pinned trades that belonged to the cleared mode
        setPinnedTradeIds(prev => {
          const remainingTrades = tradeData?.trades?.filter(trade => {
            const isPaperTrade = trade.paperTrading === true || trade.isSimulated === true || !trade.realMoney;
            return tradingMode === 'paper' ? !isPaperTrade : isPaperTrade;
          }) || [];
          const remainingIds = new Set(remainingTrades.map(t => t.id));
          return prev.filter(id => remainingIds.has(id));
        });
        setConfirmClearOpen(false);
        alert(`✅ ${result.message}`);
      } else {
        throw new Error(result.error || 'Failed to clear history');
      }
    } catch (err) {
      console.error('Error clearing history:', err);
      alert(`❌ Failed to clear history: ${err.message}`);
    } finally {
      setClearingHistory(false);
    }
  };
  
  // Fetch trade data from strategy nodes (full load)
  const fetchTradeHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get strategy ID if not 'all'
      const strategyId = selectedStrategy === 'all' ? null : selectedStrategy;
      
      // Communicate with active strategy nodes through the service
      const data = await TradeHistoryService.getTradeHistory(strategyId, dateRange);
      setTradeData(data);
      
      // Get list of active strategies
      const strategies = await TradeHistoryService.getActiveStrategies();
      setActiveStrategies(strategies);
    } catch (err) {
      console.error('Error fetching trade history data:', err);
      setError('Failed to fetch trade data from strategy nodes. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Update only P&L values without full refresh (for live updates)
  const updatePnLOnly = async () => {
    if (!tradeData || !tradeData.trades) return;
    
    try {
      const strategyId = selectedStrategy === 'all' ? null : selectedStrategy;
      const data = await TradeHistoryService.getTradeHistory(strategyId, dateRange);
      
      if (data && data.trades) {
        // Only update if we have existing data - merge P&L updates
        setTradeData(prev => {
          if (!prev) return data;
          
          // Create a map of updated trades by ID
          const updatedTradesMap = new Map(data.trades.map(t => [t.id, t]));
          
          // Detect position closes (open → closed) for sound notifications
          let closedInProfit = false;
          let closedInLoss = false;
          
          // Update existing trades with new P&L values
          const mergedTrades = prev.trades.map(trade => {
            const updated = updatedTradesMap.get(trade.id);
            if (updated) {
              // Detect status transition: open/pending → closed
              if ((trade.status === 'open' || trade.status === 'pending') && updated.status === 'closed') {
                const pnl = parseFloat(updated.pnl) || 0;
                if (pnl >= 0) closedInProfit = true;
                else closedInLoss = true;
              }
              // v4.4.2: FREEZE closed positions — once a trade is closed, its P&L values
              // are locked at the exit moment. Only allow status transition updates (open→closed)
              // and never overwrite frozen P&L with live-recalculated values.
              if (trade.status === 'closed' && updated.status === 'closed') {
                // Already closed — only update non-P&L fields (closeReason, duration etc.)
                return {
                  ...trade,
                  duration: updated.duration || trade.duration,
                  closeReason: updated.closeReason || trade.closeReason,
                };
              }
              return {
                ...trade,
                currentPrice: updated.currentPrice || updated.exitPrice,
                exitPrice: updated.exitPrice,
                pnl: updated.pnl,
                pnlPercent: updated.pnlPercent,
                status: updated.status,
                duration: updated.duration,
              };
            }
            return trade;
          });
          
          // Play close sounds (profit takes priority if both happen same tick)
          if (closedInProfit) playProfitCloseSound();
          else if (closedInLoss) playLossCloseSound();
          
          // Add any new trades that weren't in the previous list
          const existingIds = new Set(prev.trades.map(t => t.id));
          const newTrades = data.trades.filter(t => !existingIds.has(t.id));
          
          // Play sound if new trades appeared (only if no close sound played)
          if (newTrades.length > 0 && !closedInProfit && !closedInLoss) {
            playNewTradeSound();
          }
          
          return {
            ...prev,
            trades: [...newTrades, ...mergedTrades],
            summary: data.summary,
            updatedAt: data.updatedAt,
          };
        });
      }
    } catch (err) {
      console.error('Error updating P&L:', err);
    }
  };
  
  // Export trade data
  const handleExportData = async (format) => {
    try {
      await TradeHistoryService.exportTradeData(selectedStrategy, format);
      setExportAnchorEl(null);
    } catch (err) {
      setError(`Failed to export trade data as ${format.toUpperCase()}. Please try again.`);
    }
  };
  
  // Handle filter changes
  const handleStrategyChange = (event) => {
    setSelectedStrategy(event.target.value);
  };
  
  const handleDateRangeChange = (type, date) => {
    setDateRange(prev => ({
      ...prev,
      [type]: date
    }));
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Menu handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  
  // Apply filters
  const applyFilters = () => {
    fetchTradeHistory();
    handleFilterClose();
  };
  
  // Format currency values - calculate P&L from trade data if value is 0
  const formatCurrency = (value, trade = null) => {
    let num = parseFloat(value) || 0;
    
    // If P&L is 0 but we have trade data, calculate it (leverage-aware)
    if (num === 0 && trade && trade.entryPrice && trade.size) {
      const entryPrice = parseFloat(trade.entryPrice) || 0;
      const exitPrice = parseFloat(trade.exitPrice || trade.currentPrice) || entryPrice;
      const size = parseFloat(trade.size) || 0;
      const leverage = parseFloat(trade.leverage) || 1;
      const direction = trade.direction || 'buy';
      
      if (direction === 'buy' || direction === 'long') {
        num = (exitPrice - entryPrice) * size * leverage;
      } else {
        num = (entryPrice - exitPrice) * size * leverage;
      }
    }
    
    // For very small values, show more precision
    if (Math.abs(num) < 0.01 && Math.abs(num) > 0.000001) {
      return num > 0 ? `+$${num.toFixed(6)}` : `-$${Math.abs(num).toFixed(6)}`;
    }
    if (Math.abs(num) < 1 && Math.abs(num) >= 0.01) {
      return num > 0 ? `+$${num.toFixed(4)}` : `-$${Math.abs(num).toFixed(4)}`;
    }
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };
  
  // Format percentage values (limit to 2 decimal places)
  const formatPercent = (value) => {
    const num = parseFloat(value) || 0;
    const formatted = num.toFixed(2);
    return num > 0 ? `+${formatted}%` : `${formatted}%`;
  };
  
  // Format price values based on magnitude
  const formatPrice = (value) => {
    const num = parseFloat(value) || 0;
    if (num === 0) return '-';
    if (num < 0.001) return num.toFixed(8);
    if (num < 1) return num.toFixed(6);
    if (num < 100) return num.toFixed(4);
    return num.toFixed(2);
  };
  
  // Load initial data when component mounts
  useEffect(() => {
    fetchTradeHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStrategy, dateRange]);
  
  // Auto-refresh P&L only every 5 seconds (without full panel refresh)
  useEffect(() => {
    if (!tradeData) return;
    
    const interval = setInterval(() => {
      updatePnLOnly();
    }, 5000);
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeData, selectedStrategy, dateRange]);
  
  return (
    <Box>
      {/* Header with action buttons — Glassmorphic Dark */}
      <Box sx={{
        mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
        p: 2, borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white', mr: 3 }}>
            Trade History
          </Typography>
          
          {/* Paper/Real Trading Mode Toggle */}
          <Box sx={{ 
            display: 'flex', 
            bgcolor: 'rgba(0,0,0,0.3)', 
            borderRadius: 2,
            p: 0.4,
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <Button
              size="small"
              onClick={() => setTradingMode('paper')}
              sx={{ 
                borderRadius: 1.5, px: 2, fontSize: '0.78rem', textTransform: 'none',
                fontWeight: tradingMode === 'paper' ? 700 : 400,
                bgcolor: tradingMode === 'paper' ? 'rgba(59,130,246,0.2)' : 'transparent',
                color: tradingMode === 'paper' ? '#3b82f6' : '#9CA3AF',
                border: tradingMode === 'paper' ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent',
                '&:hover': { bgcolor: tradingMode === 'paper' ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)' },
              }}
            >
              <DescriptionIcon sx={{ fontSize: 15, mr: 0.5 }} /> Paper Trading
            </Button>
            <Button
              size="small"
              onClick={() => setTradingMode('real')}
              sx={{ 
                borderRadius: 1.5, px: 2, fontSize: '0.78rem', textTransform: 'none',
                fontWeight: tradingMode === 'real' ? 700 : 400,
                bgcolor: tradingMode === 'real' ? 'rgba(0,255,148,0.12)' : 'transparent',
                color: tradingMode === 'real' ? '#00FF94' : '#9CA3AF',
                border: tradingMode === 'real' ? '1px solid rgba(0,255,148,0.25)' : '1px solid transparent',
                '&:hover': { bgcolor: tradingMode === 'real' ? 'rgba(0,255,148,0.18)' : 'rgba(255,255,255,0.05)' },
              }}
            >
              <AttachMoneyIcon sx={{ fontSize: 15, mr: 0.5 }} /> Real Trading
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button 
            startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
            onClick={handleFilterClick}
            sx={{
              fontSize: '0.78rem', textTransform: 'none', px: 2, py: 0.7,
              bgcolor: 'rgba(255,255,255,0.05)', color: '#D1D5DB',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.2)' },
            }}
          >
            Filter
          </Button>
          <Button 
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExportClick}
            sx={{
              fontSize: '0.78rem', textTransform: 'none', px: 2, py: 0.7,
              bgcolor: 'rgba(255,255,255,0.05)', color: '#D1D5DB',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.2)' },
            }}
          >
            Export
          </Button>
          <IconButton onClick={fetchTradeHistory} sx={{ color: '#9CA3AF', '&:hover': { color: '#00FF94' } }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Tooltip title={soundEnabled ? "Mute new trade sounds" : "Enable new trade sounds"}>
            <IconButton size="small" onClick={() => setSoundEnabled(!soundEnabled)} sx={{ color: soundEnabled ? '#3b82f6' : '#6B7280' }}>
              {soundEnabled ? <VolumeUpIcon sx={{ fontSize: 18 }} /> : <VolumeOffIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
          <Button 
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            onClick={() => setConfirmCloseAllOpen(true)}
            disabled={!tradeData?.trades?.some(t => t.status === 'open' || t.status === 'pending')}
            sx={{
              fontSize: '0.78rem', textTransform: 'none', px: 2, py: 0.7,
              bgcolor: 'rgba(239,68,68,0.12)', color: '#EF4444',
              border: '1px solid rgba(239,68,68,0.25)', borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(239,68,68,0.2)' },
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            Close All Positions
          </Button>
          
          <Button 
            startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
            onClick={() => setConfirmClearOpen(true)}
            sx={{
              fontSize: '0.78rem', textTransform: 'none', px: 2, py: 0.7,
              bgcolor: 'rgba(239,68,68,0.08)', color: '#EF4444',
              border: '1px solid rgba(239,68,68,0.2)', borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' },
            }}
          >
            Clear History
          </Button>
          
          {/* Confirm Close All Dialog */}
          <Dialog open={confirmCloseAllOpen} onClose={() => setConfirmCloseAllOpen(false)}>
            <DialogTitle>⚠️ Close All Open Positions?</DialogTitle>
            <DialogContent>
              <Typography>
                This will close all open and pending positions at current market prices.
                {tradeData?.trades && (
                  <Box sx={{ mt: 2 }}>
                    <strong>Positions to close: </strong>
                    {tradeData.trades.filter(t => t.status === 'open' || t.status === 'pending').length}
                  </Box>
                )}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setConfirmCloseAllOpen(false)}>Cancel</Button>
                <Button 
                  variant="contained" 
                  color="warning" 
                  onClick={handleCloseAllPositions}
                  disabled={closingAllPositions}
                  startIcon={closingAllPositions ? <CircularProgress size={16} /> : <CloseIcon />}
                >
                  {closingAllPositions ? 'Closing...' : 'Close All'}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
          
          {/* Confirm Clear Dialog */}
          <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
            <DialogTitle>⚠️ Clear {tradingMode === 'paper' ? 'Paper' : 'Real'} Trade History?</DialogTitle>
            <DialogContent>
              <Typography>
                This will permanently delete all <strong>{tradingMode === 'paper' ? 'paper trading' : 'real trading'}</strong> history and positions.
                {tradingMode === 'real' ? ' Real money positions will be closed.' : ' Simulated positions will be removed.'}
                This action cannot be undone.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
                <Button 
                  variant="contained" 
                  color="error" 
                  onClick={handleClearHistory}
                  disabled={clearingHistory}
                  startIcon={clearingHistory ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
                >
                  {clearingHistory ? 'Clearing...' : 'Clear All'}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
          
          {/* Bulk Close Confirm Dialog */}
          <Dialog open={bulkCloseConfirmOpen} onClose={() => !bulkCloseExecuting && setBulkCloseConfirmOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              ⚠️ Bulk Close — {bulkCloseFilterType === 'PROFIT_ONLY' ? 'All Profitable' : bulkCloseFilterType === 'LOSS_ONLY' ? 'All Losing' : bulkCloseFilterType === 'PROFIT_AT_LEAST' ? `P&L ≥ +${bulkCloseThreshold}%` : `P&L ≤ -${bulkCloseThreshold}%`}
            </DialogTitle>
            <DialogContent>
              {!bulkClosePreview ? (
                <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /><Typography sx={{ mt: 1 }}>Loading preview...</Typography></Box>
              ) : (
                <Box>
                  <Alert severity={bulkClosePreview.eligibleCount > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
                    <strong>{bulkClosePreview.eligibleCount}</strong> position{bulkClosePreview.eligibleCount !== 1 ? 's' : ''} will be closed at market price.
                    {bulkClosePreview.totalNotionalUsd > 0 && (
                      <> Est. notional: <strong>${bulkClosePreview.totalNotionalUsd.toFixed(2)}</strong></>
                    )}
                  </Alert>
                  {bulkClosePreview.positions?.length > 0 && (
                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Side</TableCell>
                            <TableCell align="right">P&L %</TableCell>
                            <TableCell align="right">Net P&L</TableCell>
                            <TableCell align="right">Notional</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bulkClosePreview.positions.map((p, i) => (
                            <TableRow key={i}>
                              <TableCell><Chip size="small" label={p.symbol} /></TableCell>
                              <TableCell><Chip size="small" label={(p.side || '').toUpperCase()} color={p.side === 'buy' ? 'success' : 'error'} /></TableCell>
                              <TableCell align="right" sx={{ color: p.pnlPct > 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>{p.pnlPct?.toFixed(2)}%</TableCell>
                              <TableCell align="right" sx={{ color: p.netPnl > 0 ? 'success.main' : 'error.main' }}>${parseFloat(p.netPnl || 0).toFixed(4)}</TableCell>
                              <TableCell align="right">${p.notionalUsd?.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setBulkCloseConfirmOpen(false)} disabled={bulkCloseExecuting}>Cancel</Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleBulkCloseExecute}
                  disabled={bulkCloseExecuting || !bulkClosePreview || bulkClosePreview.eligibleCount === 0}
                  startIcon={bulkCloseExecuting ? <CircularProgress size={16} /> : <CloseIcon />}
                >
                  {bulkCloseExecuting ? 'Closing...' : `Close ${bulkClosePreview?.eligibleCount || 0} Position${(bulkClosePreview?.eligibleCount || 0) !== 1 ? 's' : ''}`}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Bulk Close Results Dialog */}
          <Dialog open={bulkCloseResultsOpen} onClose={() => setBulkCloseResultsOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk Close Results</DialogTitle>
            <DialogContent>
              {bulkCloseResults && (
                <Box>
                  <Alert severity={bulkCloseResults.failedCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    {bulkCloseResults.message}
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip label={`Closed: ${bulkCloseResults.closedCount}`} color="success" />
                    {bulkCloseResults.failedCount > 0 && <Chip label={`Failed: ${bulkCloseResults.failedCount}`} color="error" />}
                    <Chip label={`Targeted: ${bulkCloseResults.targeted}`} variant="outlined" />
                  </Box>
                  {bulkCloseResults.results?.length > 0 && (
                    <TableContainer sx={{ maxHeight: 250 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">P&L</TableCell>
                            <TableCell>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bulkCloseResults.results.map((r, i) => (
                            <TableRow key={i}>
                              <TableCell>{r.symbol}</TableCell>
                              <TableCell><Chip size="small" label={r.status} color={r.status === 'closed' ? 'success' : 'error'} /></TableCell>
                              <TableCell align="right" sx={{ color: (r.pnl || 0) > 0 ? 'success.main' : 'error.main' }}>{r.pnl != null ? `$${r.pnl.toFixed(4)}` : '—'}</TableCell>
                              <TableCell><Typography variant="caption" color="error">{r.error || ''}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={() => setBulkCloseResultsOpen(false)}>Close</Button>
              </Box>
            </DialogContent>
          </Dialog>
          
          {/* Filter Menu */}
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1, width: 300 }}>
              <Typography variant="subtitle1" gutterBottom>Filters</Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="strategy-select-label">Strategy</InputLabel>
                <Select
                  labelId="strategy-select-label"
                  value={selectedStrategy}
                  label="Strategy"
                  onChange={handleStrategyChange}
                >
                  <MenuItem value="all">All Strategies</MenuItem>
                  {activeStrategies.map(strategy => (
                    <MenuItem key={strategy.id} value={strategy.id}>
                      {strategy.name} ({strategy.market})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="subtitle2" gutterBottom>Date Range</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="From"
                    type="date"
                    value={dateRange.start || ''}
                    onChange={(e) => handleDateRangeChange('start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To"
                    type="date"
                    value={dateRange.end || ''}
                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button onClick={handleFilterClose} sx={{ mr: 1 }}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={applyFilters}>Apply</Button>
              </Box>
            </Box>
          </Menu>
          
          {/* Export Menu */}
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={() => handleExportData('csv')}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExportData('xlsx')}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Excel</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExportData('json')}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>JSON</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Market Sessions Monitor - Interactive Timeline */}
      <Box sx={{ mb: 2 }}>
        <MarketSessionsMonitor compact={false} />
      </Box>

      {/* Bulk Close Controls — Glassmorphic Dark */}
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        flexWrap: 'wrap',
        p: 1.5,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.8) 0%, rgba(24,27,33,0.85) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 2,
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mr: 1, color: '#FBBF24' }}>
          Bulk Close:
        </Typography>
        <Tooltip title="Close all profitable positions (P&L > 0%)">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => handleBulkCloseOpen('PROFIT_ONLY')}
              disabled={bulkCloseCounts.PROFIT_ONLY === 0}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, textTransform: 'none' }}
            >
              Close Profit ({bulkCloseCounts.PROFIT_ONLY})
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Close all losing positions (P&L < 0%)">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleBulkCloseOpen('LOSS_ONLY')}
              disabled={bulkCloseCounts.LOSS_ONLY === 0}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, textTransform: 'none' }}
            >
              Close Loss ({bulkCloseCounts.LOSS_ONLY})
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, pl: 1, borderLeft: '1px solid rgba(255,255,255,0.15)' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>Threshold:</Typography>
          <TextField
            size="small"
            type="number"
            value={bulkCloseThreshold}
            onChange={(e) => {
              const v = Math.max(0.1, Math.min(999, parseFloat(e.target.value) || 5));
              setBulkCloseThreshold(v);
            }}
            inputProps={{ min: 0.1, max: 999, step: 0.5 }}
            sx={{ width: 70, '& .MuiInputBase-input': { py: 0.5, px: 0.8, fontSize: '0.8rem' } }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>%</Typography>
        </Box>
        <Tooltip title={`Close positions with P&L ≥ +${bulkCloseThreshold}%`}>
          <span>
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => handleBulkCloseOpen('PROFIT_AT_LEAST')}
              disabled={bulkCloseCounts.PROFIT_AT_LEAST === 0}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, textTransform: 'none', borderStyle: 'dashed' }}
            >
              ≥ +{bulkCloseThreshold}% ({bulkCloseCounts.PROFIT_AT_LEAST})
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={`Close positions with P&L ≤ -${bulkCloseThreshold}%`}>
          <span>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleBulkCloseOpen('LOSS_AT_MOST')}
              disabled={bulkCloseCounts.LOSS_AT_MOST === 0}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, textTransform: 'none', borderStyle: 'dashed' }}
            >
              ≤ -{bulkCloseThreshold}% ({bulkCloseCounts.LOSS_AT_MOST})
            </Button>
          </span>
        </Tooltip>

        {/* Accept All / Decline All Signals */}
        {(() => {
          const signalCount = tradeData?.trades?.filter(t => t.status === 'signal' && (tradingMode === 'paper' ? (t.paperTrading === true || t.isSimulated === true || !t.realMoney) : !(t.paperTrading === true || t.isSimulated === true || !t.realMoney))).length || 0;
          if (signalCount === 0) return null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, pl: 1, borderLeft: '1px solid rgba(255,152,0,0.3)' }}>
              <Tooltip title={`Execute all ${signalCount} signals at live price`}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={bulkSignalExecuting || bulkSignalDismissing}
                    onClick={async () => {
                      if (!window.confirm(`Execute ALL ${signalCount} signals at live prices?\n\nThis will open ${signalCount} new positions.`)) return;
                      setBulkSignalExecuting(true);
                      try {
                        const signals = tradeData.trades.filter(t => t.status === 'signal');
                        for (const sig of signals) {
                          try { await StrategyEngineService.executeSignal(sig.id); } catch (e) { console.error(`Failed to execute ${sig.symbol}:`, e); }
                        }
                        fetchTradeHistory();
                      } finally { setBulkSignalExecuting(false); }
                    }}
                    sx={{ fontSize: '0.7rem', py: 0.3, px: 1, textTransform: 'none', minWidth: 0 }}
                    startIcon={bulkSignalExecuting ? <CircularProgress size={12} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
                  >
                    Accept All ({signalCount})
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={`Dismiss all ${signalCount} signals`}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={bulkSignalExecuting || bulkSignalDismissing}
                    onClick={async () => {
                      if (!window.confirm(`Dismiss ALL ${signalCount} signals?\n\nThey will be removed from the list.`)) return;
                      setBulkSignalDismissing(true);
                      try {
                        const signals = tradeData.trades.filter(t => t.status === 'signal');
                        for (const sig of signals) {
                          try { await StrategyEngineService.dismissSignal(sig.id); } catch (e) { console.error(`Failed to dismiss ${sig.symbol}:`, e); }
                        }
                        fetchTradeHistory();
                      } finally { setBulkSignalDismissing(false); }
                    }}
                    sx={{ fontSize: '0.7rem', py: 0.3, px: 1, textTransform: 'none', minWidth: 0 }}
                    startIcon={bulkSignalDismissing ? <CircularProgress size={12} /> : <NotInterestedIcon sx={{ fontSize: 14 }} />}
                  >
                    Decline All ({signalCount})
                  </Button>
                </span>
              </Tooltip>
            </Box>
          );
        })()}

        {/* Divider + Global Exit Policy Toggles */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          ml: 'auto',
          pl: 2,
          borderLeft: '1px solid rgba(255,255,255,0.1)',
          opacity: bulkUpdating ? 0.5 : 1
        }}>
          <SmartToyIcon sx={{ fontSize: 16, color: '#A855F7' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#A855F7', whiteSpace: 'nowrap' }}>
            Global Exit Policy:
          </Typography>
          <Tooltip title="Toggle Take Profit for ALL open positions">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography variant="caption" sx={{ color: globalTpEnabled ? 'success.main' : 'text.disabled', fontWeight: 'bold' }}>TP</Typography>
              <Switch
                checked={globalTpEnabled}
                onChange={(e) => handleBulkTpSlToggle('autoTpEnabled', e.target.checked)}
                color="success"
                size="small"
                disabled={bulkUpdating || !tradeData?.trades?.some(t => t.status === 'open' || t.status === 'pending')}
                sx={{ 
                  '& .MuiSwitch-switchBase': { p: '3px' },
                  '& .MuiSwitch-thumb': { width: 14, height: 14 },
                  width: 38, height: 22
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Toggle Stop Loss for ALL open positions">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography variant="caption" sx={{ color: globalSlEnabled ? 'error.main' : 'text.disabled', fontWeight: 'bold' }}>SL</Typography>
              <Switch
                checked={globalSlEnabled}
                onChange={(e) => handleBulkTpSlToggle('autoSlEnabled', e.target.checked)}
                color="error"
                size="small"
                disabled={bulkUpdating || !tradeData?.trades?.some(t => t.status === 'open' || t.status === 'pending')}
                sx={{ 
                  '& .MuiSwitch-switchBase': { p: '3px' },
                  '& .MuiSwitch-thumb': { width: 14, height: 14 },
                  width: 38, height: 22
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Toggle AI Exit for ALL open positions">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography variant="caption" sx={{ color: globalAiExitEnabled ? 'info.main' : 'text.disabled', fontWeight: 'bold' }}>AI</Typography>
              <Switch
                checked={globalAiExitEnabled}
                onChange={(e) => handleBulkTpSlToggle('autoAiExitEnabled', e.target.checked)}
                color="info"
                size="small"
                disabled={bulkUpdating || !tradeData?.trades?.some(t => t.status === 'open' || t.status === 'pending')}
                sx={{ 
                  '& .MuiSwitch-switchBase': { p: '3px' },
                  '& .MuiSwitch-thumb': { width: 14, height: 14 },
                  width: 38, height: 22
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Toggle ALL (TP + SL + AI) for ALL open positions">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography variant="caption" sx={{ color: (globalTpEnabled && globalSlEnabled && globalAiExitEnabled) ? 'secondary.main' : 'text.disabled', fontWeight: 'bold' }}>All</Typography>
              <Switch
                checked={globalTpEnabled && globalSlEnabled && globalAiExitEnabled}
                onChange={async (e) => {
                  const val = e.target.checked;
                  setBulkUpdating(true);
                  try {
                    const openT = tradeData?.trades?.filter(t => t.status === 'open' || t.status === 'pending') || [];
                    const sId = openT[0]?.strategyId || null;
                    await StrategyEngineService.bulkUpdatePositionSettings({ autoTpEnabled: val, autoSlEnabled: val, autoAiExitEnabled: val, ...(sId && { strategyId: sId }) });
                    setTradeData(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        trades: prev.trades.map(t => {
                          if (t.status === 'open' || t.status === 'pending') {
                            return { ...t, autoTpEnabled: val, autoSlEnabled: val, autoAiExitEnabled: val };
                          }
                          return t;
                        })
                      };
                    });
                    setGlobalTpEnabled(val);
                    setGlobalSlEnabled(val);
                    setGlobalAiExitEnabled(val);
                  } catch (err) {
                    console.error('Bulk All TP/SL/AI update failed:', err);
                  } finally {
                    setBulkUpdating(false);
                  }
                }}
                color="secondary"
                size="small"
                disabled={bulkUpdating || !tradeData?.trades?.some(t => t.status === 'open' || t.status === 'pending')}
                sx={{ 
                  '& .MuiSwitch-switchBase': { p: '3px' },
                  '& .MuiSwitch-thumb': { width: 14, height: 14 },
                  width: 38, height: 22
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading trade history from strategy nodes...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      ) : tradeData ? (
        <Box>
          {/* Mode indicator banner — glassmorphic dark */}
          <Box sx={{
            mb: 3, px: 2.5, py: 1.5, borderRadius: 2,
            background: tradingMode === 'paper'
              ? 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(30,27,75,0.3) 100%)'
              : 'linear-gradient(135deg, rgba(0,255,148,0.06) 0%, rgba(30,27,75,0.3) 100%)',
            border: `1px solid ${tradingMode === 'paper' ? 'rgba(59,130,246,0.2)' : 'rgba(0,255,148,0.15)'}`,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              p: 0.8, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1.5,
              border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {tradingMode === 'paper'
                ? <DescriptionIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                : <AttachMoneyIcon sx={{ fontSize: 18, color: '#00FF94' }} />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                {tradingMode === 'paper' ? 'Paper Trading Mode' : 'Real Trading Mode'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', lineHeight: 1.3 }}>
                {tradingMode === 'paper'
                  ? 'Showing simulated trades for practice and testing strategies.'
                  : 'Showing real trades with actual money on connected brokers.'}
              </Typography>
            </Box>
          </Box>
          
          {/* Compute filtered stats */}
          {(() => {
            const filteredTrades = tradeData.trades.filter(trade => {
              const isPaperTrade = trade.paperTrading === true || trade.isSimulated === true || !trade.realMoney;
              return tradingMode === 'paper' ? isPaperTrade : !isPaperTrade;
            });
            const openPositions = filteredTrades.filter(t => t.status === 'open' || t.status === 'pending');
            const closedTrades = filteredTrades.filter(t => t.status === 'closed');
            
            // Calculate REALIZED P&L (closed trades only)
            const realizedPnl = closedTrades.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
            // Calculate UNREALIZED P&L (open positions)
            const unrealizedPnl = openPositions.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
            // Total P&L = Realized + Unrealized
            const totalPnl = realizedPnl + unrealizedPnl;
            
            const openTrades = openPositions.length;
            const winningTrades = closedTrades.filter(t => parseFloat(t.pnl) > 0);
            const winRate = closedTrades.length > 0 ? (winningTrades.length / closedTrades.length * 100).toFixed(1) : '0.0';
            
            // Calculate monthly return from filtered trades only
            const now = new Date();
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const monthlyTrades = closedTrades.filter(t => new Date(t.date) >= monthStart);
            const monthlyPnl = monthlyTrades.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
            const monthlyReturn = filteredTrades.length > 0 ? ((monthlyPnl / 1000) * 100).toFixed(2) : '0.00'; // Assuming $1000 base
            
            // Calculate YTD return from filtered trades only
            const yearStart = new Date(now.getFullYear(), 0, 1);
            const ytdTrades = closedTrades.filter(t => new Date(t.date) >= yearStart);
            const ytdPnl = ytdTrades.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
            const ytdReturn = filteredTrades.length > 0 ? ((ytdPnl / 1000) * 100).toFixed(2) : '0.00';
            
            return (
              <>
              {/* Summary Cards — Glassmorphic Dark Theme */}
              <Grid container spacing={2} sx={{ mb: 4, alignItems: 'stretch' }}>
                {/* Total Trades */}
                <Grid item xs={12} sm={6} lg={3}>
                  <Box sx={{
                    background: 'rgba(24,27,33,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, p: 2.5, position: 'relative', overflow: 'hidden',
                    transition: 'all 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.12)', transform: 'translateY(-2px)' },
                  }}>
                    <Box sx={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, background: '#3b82f6', borderRadius: '0 4px 4px 0', boxShadow: '0 0 10px rgba(59,130,246,0.6)' }} />
                    <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 96, height: 96, background: 'rgba(59,130,246,0.08)', borderRadius: '50%', filter: 'blur(24px)' }} />
                    <Box sx={{ position: 'absolute', top: 12, right: 12, opacity: 0.08 }}>
                      <ShowChartIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                      Total Trades
                    </Typography>
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '1.8rem', color: 'white', lineHeight: 1 }}>
                      {filteredTrades.length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'white', fontWeight: 600 }}>{openTrades}</Box> open positions
                    </Typography>
                  </Box>
                </Grid>

                {/* Realized P&L */}
                <Grid item xs={12} sm={6} lg={3}>
                  {(() => {
                    const pnlColor = realizedPnl > 0 ? '#00FF94' : realizedPnl < 0 ? '#EF4444' : 'white';
                    const pnlGlow = realizedPnl > 0 ? 'rgba(0,255,148,0.4)' : realizedPnl < 0 ? 'rgba(255,59,59,0.4)' : 'none';
                    return (
                      <Box sx={{
                        background: 'rgba(24,27,33,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 2, p: 2.5, position: 'relative', overflow: 'hidden',
                        transition: 'all 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.12)', transform: 'translateY(-2px)' },
                      }}>
                        <Box sx={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, background: pnlColor, borderRadius: '0 4px 4px 0', boxShadow: `0 0 10px ${pnlGlow}` }} />
                        <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 96, height: 96, background: `${pnlColor}15`, borderRadius: '50%', filter: 'blur(24px)' }} />
                        <Box sx={{ position: 'absolute', top: 12, right: 12, opacity: 0.08 }}>
                          {realizedPnl >= 0
                            ? <TrendingUpIcon sx={{ fontSize: 36 }} />
                            : <TrendingDownIcon sx={{ fontSize: 36 }} />}
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                          Realized P&L
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{
                            fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '1.8rem', lineHeight: 1,
                            color: pnlColor, textShadow: `0 0 10px ${pnlGlow}`,
                          }}>
                            {realizedPnl >= 0 ? '+' : ''}{formatCurrency(realizedPnl)}
                          </Typography>
                          {realizedPnl > 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 22, color: '#00FF94' }} />
                          ) : realizedPnl < 0 ? (
                            <TrendingDownIcon sx={{ fontSize: 22, color: '#EF4444' }} />
                          ) : null}
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mt: 1.5, fontFamily: '"JetBrains Mono", monospace' }}>
                          Win Rate: {winRate}%
                        </Typography>
                        {openTrades > 0 && (
                          <Typography sx={{
                            fontSize: '0.7rem', mt: 0.3,
                            color: unrealizedPnl >= 0 ? 'rgba(0,255,148,0.7)' : '#EF4444',
                            fontFamily: '"JetBrains Mono", monospace',
                          }}>
                            Unrealized: {unrealizedPnl >= 0 ? '+' : ''}{formatCurrency(unrealizedPnl)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </Grid>

                {/* Monthly Return */}
                <Grid item xs={12} sm={6} lg={3}>
                  {(() => {
                    const mVal = parseFloat(monthlyReturn);
                    const mColor = mVal > 0 ? '#00FF94' : mVal < 0 ? '#EF4444' : 'white';
                    const mGlow = mVal > 0 ? 'rgba(0,255,148,0.4)' : mVal < 0 ? 'rgba(255,59,59,0.4)' : 'none';
                    return (
                      <Box sx={{
                        background: 'rgba(24,27,33,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, p: 2.5, position: 'relative', overflow: 'hidden',
                        transition: 'all 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.12)', transform: 'translateY(-2px)' },
                      }}>
                        <Box sx={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, background: mColor, borderRadius: '0 4px 4px 0', boxShadow: `0 0 10px ${mGlow}` }} />
                        <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 96, height: 96, background: `${mColor}15`, borderRadius: '50%', filter: 'blur(24px)' }} />
                        <Box sx={{ position: 'absolute', top: 12, right: 12, opacity: 0.08 }}>
                          <BarChartIcon sx={{ fontSize: 36 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                          Monthly Return
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{
                            fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '1.8rem', lineHeight: 1,
                            color: mColor, textShadow: `0 0 10px ${mGlow}`,
                          }}>
                            {mVal >= 0 ? '+' : ''}{monthlyReturn}%
                          </Typography>
                          {mVal > 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 22, color: '#00FF94' }} />
                          ) : mVal < 0 ? (
                            <TrendingDownIcon sx={{ fontSize: 22, color: '#EF4444' }} />
                          ) : null}
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mt: 1.5, fontFamily: '"JetBrains Mono", monospace' }}>
                          YTD: {parseFloat(ytdReturn) >= 0 ? '+' : ''}{ytdReturn}%
                        </Typography>
                      </Box>
                    );
                  })()}
                </Grid>

                {/* Paper/Real Stats */}
                <Grid item xs={12} sm={6} lg={3}>
                  {(() => {
                    const statsColor = tradingMode === 'paper' ? '#3b82f6' : '#00FF94';
                    return (
                  <Box sx={{
                    background: 'rgba(24,27,33,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, p: 2.5, position: 'relative', overflow: 'hidden',
                    transition: 'all 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.12)', transform: 'translateY(-2px)' },
                  }}>
                    <Box sx={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, background: statsColor, borderRadius: '0 4px 4px 0', boxShadow: `0 0 10px ${statsColor}99` }} />
                    <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 96, height: 96, background: `${statsColor}15`, borderRadius: '50%', filter: 'blur(24px)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                      {tradingMode === 'paper'
                        ? <DescriptionIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                        : <AttachMoneyIcon sx={{ fontSize: 16, color: '#00FF94' }} />}
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        {tradingMode === 'paper' ? 'Paper Stats' : 'Real Stats'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Closed:</Typography>
                      <Typography sx={{ fontSize: '0.8rem', fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, color: 'white' }}>
                        {closedTrades.length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Winning:</Typography>
                      <Typography sx={{ fontSize: '0.8rem', fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, color: '#00FF94' }}>
                        {winningTrades.length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Losing:</Typography>
                      <Typography sx={{ fontSize: '0.8rem', fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, color: '#EF4444' }}>
                        {closedTrades.length - winningTrades.length}
                      </Typography>
                    </Box>
                  </Box>
                    );
                  })()}
                </Grid>
              </Grid>
              
              {/* Per-Strategy Breakdown — Glassmorphic Dark Theme */}
              {(() => {
                const strategyStats = {};
                filteredTrades.forEach(t => {
                  const name = t.strategyName || t.strategySnapshot?.name || `Strategy ${t.strategyId?.slice(-6) || t.runId?.slice(-6) || 'Unknown'}`;
                  const key = t.strategyId || t.runId || name;
                  if (!strategyStats[key]) {
                    strategyStats[key] = { name, trades: [], closed: [], open: [], firstTradeAt: null };
                  }
                  strategyStats[key].trades.push(t);
                  if (t.status === 'closed') strategyStats[key].closed.push(t);
                  if (t.status === 'open' || t.status === 'pending') strategyStats[key].open.push(t);
                  // Track earliest trade time for sorting
                  const tradeTime = t.openedAt || t.createdAt || t.entryTime;
                  if (tradeTime && (!strategyStats[key].firstTradeAt || tradeTime < strategyStats[key].firstTradeAt)) {
                    strategyStats[key].firstTradeAt = tradeTime;
                  }
                });
                
                let keys = Object.keys(strategyStats);
                if (keys.length < 1) return null;
                
                // Sort keys based on selected mode
                if (strategySortMode === 'profitable') {
                  keys.sort((a, b) => {
                    const pnlA = strategyStats[a].closed.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
                    const pnlB = strategyStats[b].closed.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
                    return pnlB - pnlA;
                  });
                } else if (strategySortMode === 'oldest') {
                  keys.sort((a, b) => {
                    const tA = strategyStats[a].firstTradeAt || '9999';
                    const tB = strategyStats[b].firstTradeAt || '9999';
                    return tA < tB ? -1 : tA > tB ? 1 : 0;
                  });
                } else if (strategySortMode === 'newest') {
                  keys.sort((a, b) => {
                    const tA = strategyStats[a].firstTradeAt || '0000';
                    const tB = strategyStats[b].firstTradeAt || '0000';
                    return tB < tA ? -1 : tB > tA ? 1 : 0;
                  });
                }
                
                // Strategy tag colors (cycle through)
                const tagColors = [
                  { bg: 'rgba(16,185,129,0.12)', text: '#00FF94', border: 'rgba(16,185,129,0.25)' },
                  { bg: 'rgba(59,130,246,0.12)', text: '#3b82f6', border: 'rgba(59,130,246,0.25)' },
                  { bg: 'rgba(139,92,246,0.12)', text: '#A855F7', border: 'rgba(139,92,246,0.25)' },
                  { bg: 'rgba(239,68,68,0.12)', text: '#EF4444', border: 'rgba(239,68,68,0.25)' },
                  { bg: 'rgba(245,158,11,0.12)', text: '#FBBF24', border: 'rgba(245,158,11,0.25)' },
                  { bg: 'rgba(6,182,212,0.12)', text: '#3b82f6', border: 'rgba(6,182,212,0.25)' },
                ];
                
                const miniToggleSx = {
                  '& .MuiSwitch-switchBase': { padding: '2px' },
                  '& .MuiSwitch-thumb': { width: 10, height: 10 },
                  '& .MuiSwitch-track': { borderRadius: 2 },
                  width: 30, height: 16,
                };
                
                return (
                  <Box sx={{ mb: 4 }}>
                    {/* Section header row with sorting controls */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
                      <Box 
                        onClick={() => setStrategyBreakdownOpen(prev => { const next = !prev; localStorage.setItem('strategyBreakdownOpen', String(next)); return next; })}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.8 } }}
                      >
                        {strategyBreakdownOpen ? <ExpandMoreIcon sx={{ fontSize: 20, color: '#9CA3AF' }} /> : <ChevronRightIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />}
                        <AccountTreeIcon sx={{ fontSize: 20, color: '#00FF94' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: 'white' }}>
                          Per-Strategy Breakdown
                        </Typography>
                      </Box>
                      {strategyBreakdownOpen && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {['profitable', 'oldest', 'newest'].map(mode => (
                            <Chip
                              key={mode}
                              label={mode === 'profitable' ? 'Most Profitable' : mode === 'oldest' ? 'Oldest' : 'Newest'}
                              size="small"
                              variant={strategySortMode === mode ? 'filled' : 'outlined'}
                              color={strategySortMode === mode ? 'success' : 'default'}
                              onClick={() => { setStrategySortMode(mode); localStorage.setItem('strategySortMode', mode); }}
                              sx={{
                                fontSize: '0.6rem', height: 22, cursor: 'pointer',
                                '& .MuiChip-label': { px: 1 },
                                ...(strategySortMode !== mode && {
                                  borderColor: 'rgba(255,255,255,0.15)', color: '#9CA3AF',
                                  '&:hover': { borderColor: 'rgba(255,255,255,0.3)' },
                                }),
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    
                    <Collapse in={strategyBreakdownOpen}>
                    <Grid container spacing={3}>
                      {keys.map((key, keyIdx) => {
                        const s = strategyStats[key];
                        const sPnl = s.closed.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
                        const sUnrealized = s.open.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
                        const sWins = s.closed.filter(t => parseFloat(t.pnl) > 0).length;
                        const sLosses = s.closed.length - sWins;
                        const sWinRate = s.closed.length > 0 ? (sWins / s.closed.length * 100) : 0;
                        const sAvgPnl = s.closed.length > 0 ? (sPnl / s.closed.length) : 0;
                        const isProfit = sPnl >= 0;
                        const isLoss = sPnl < 0;
                        const tagColor = tagColors[keyIdx % tagColors.length];
                        const pnlColor = isProfit ? '#00FF94' : '#EF4444';
                        const pnlGlow = isProfit ? 'rgba(0,255,148,0.4)' : 'rgba(255,59,59,0.4)';
                        const tagId = `#${s.name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}`;
                        
                        // Look up execution status for this strategy
                        const execEntry = (executionStatus?.strategies || []).find(es => es.strategyId === key);
                        const isScheduled = execEntry?.enabled;
                        const hasExecStatus = !!execEntry;
                        const isPaperOn = execEntry?.paperTrading !== false;
                        const isRealOn = execEntry?.realTrading === true;
                        const activatedAt = execEntry?.activatedAt || execEntry?.createdAt || s.firstTradeAt;
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                            <Box sx={{
                              background: '#181B21',
                              border: '1px solid rgba(255,255,255,0.05)',
                              borderRadius: 2,
                              p: 3,
                              position: 'relative',
                              overflow: 'hidden',
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 4px 6px -1px rgba(0,0,0,0.3)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: isLoss ? 'rgba(255,59,59,0.3)' : 'rgba(0,255,148,0.3)',
                                boxShadow: isLoss
                                  ? '0 0 15px rgba(255,59,59,0.05), inset 0 0 0 1px rgba(255,59,59,0.1)'
                                  : '0 0 15px rgba(0,255,148,0.05), inset 0 0 0 1px rgba(0,255,148,0.1)',
                                transform: 'translateY(-2px)',
                              },
                            }}>
                              {/* Loss card overlay */}
                              {isLoss && (
                                <Box sx={{
                                  position: 'absolute', inset: 0, bgcolor: 'rgba(255,59,59,0.03)',
                                  opacity: 0, transition: 'opacity 0.3s', pointerEvents: 'none', borderRadius: 2,
                                  '.MuiBox-root:hover > &': { opacity: 1 },
                                }} />
                              )}
                              
                              {/* Top row: Name + P&L */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, position: 'relative', zIndex: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'white', lineHeight: 1.2, mb: 0.5 }}>
                                    {s.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                    <Box sx={{
                                      px: 1, py: 0.3, borderRadius: 0.5,
                                      fontSize: '0.6rem', fontFamily: '"JetBrains Mono", monospace', fontWeight: 500,
                                      bgcolor: tagColor.bg, color: tagColor.text, border: `1px solid ${tagColor.border}`,
                                    }}>
                                      {tagId}
                                    </Box>
                                    {hasExecStatus && (
                                      <Tooltip title={isScheduled ? "Deactivate strategy" : "Activate strategy"}>
                                        <Switch
                                          size="small"
                                          checked={!!isScheduled}
                                          onChange={(e) => toggleStrategySchedule(key, e)}
                                          onClick={(e) => e.stopPropagation()}
                                          color="success"
                                          sx={{
                                            '& .MuiSwitch-switchBase': { padding: '3px' },
                                            '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                            '& .MuiSwitch-track': { borderRadius: 10 },
                                            width: 38, height: 22,
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {isScheduled && (
                                      <Chip label="RUNNING" size="small" color="success"
                                        sx={{ fontSize: '0.55rem', height: 16, '& .MuiChip-label': { px: 0.8 } }} />
                                    )}
                                  </Box>
                                  {/* Running timer */}
                                  {hasExecStatus && (
                                    <StrategyRunTimer activatedAt={activatedAt} isRunning={!!isScheduled} />
                                  )}
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                  <Typography sx={{
                                    fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
                                    fontSize: '1.25rem', color: pnlColor, lineHeight: 1.2,
                                    textShadow: `0 0 10px ${pnlGlow}`,
                                  }}>
                                    {isProfit ? '+' : ''}{formatCurrency(sPnl)}
                                  </Typography>
                                  {s.open.length > 0 && (
                                    <Typography sx={{
                                      fontSize: '0.7rem', color: sUnrealized >= 0 ? 'rgba(0,255,148,0.7)' : '#EF4444',
                                      fontFamily: '"JetBrains Mono", monospace', mt: 0.3,
                                    }}>
                                      Unrealized: {sUnrealized >= 0 ? '+' : ''}{formatCurrency(sUnrealized)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              
                              {/* Paper / Real Trading Toggles */}
                              {hasExecStatus && (
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 2, mb: 1.5,
                                  py: 0.8, px: 1.2, borderRadius: 1.5,
                                  bgcolor: 'rgba(15,17,21,0.6)', border: '1px solid rgba(255,255,255,0.04)',
                                  position: 'relative', zIndex: 1,
                                }}>
                                  <Tooltip title={isPaperOn ? "Disable paper trading" : "Enable paper trading"}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                      <DescriptionIcon sx={{ fontSize: 12, color: isPaperOn ? '#3b82f6' : '#4B5563' }} />
                                      <Typography sx={{ fontSize: '0.58rem', color: isPaperOn ? '#3b82f6' : '#6B7280', fontWeight: 600, userSelect: 'none' }}>Paper</Typography>
                                      <Switch
                                        size="small"
                                        checked={isPaperOn}
                                        onChange={() => handleTradingModeToggle(key, 'paperTrading', !isPaperOn)}
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={!isScheduled}
                                        sx={{
                                          ...miniToggleSx,
                                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3b82f6' },
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                  <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.08)' }} />
                                  <Tooltip title={isRealOn ? "Disable real trading (uses live broker budget)" : "Enable real trading (uses live broker budget)"}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                      <AttachMoneyIcon sx={{ fontSize: 12, color: isRealOn ? '#00FF94' : '#4B5563' }} />
                                      <Typography sx={{ fontSize: '0.58rem', color: isRealOn ? '#00FF94' : '#6B7280', fontWeight: 600, userSelect: 'none' }}>Real</Typography>
                                      <Switch
                                        size="small"
                                        checked={isRealOn}
                                        onChange={() => handleTradingModeToggle(key, 'realTrading', !isRealOn)}
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={!isScheduled}
                                        color="success"
                                        sx={miniToggleSx}
                                      />
                                    </Box>
                                  </Tooltip>
                                </Box>
                              )}
                              
                              {/* Win/Loss ratio bar */}
                              <Box sx={{ mb: 2.5, position: 'relative', zIndex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                  <Typography sx={{ fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace', color: '#9CA3AF' }}>
                                    Win/Loss Ratio
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace', color: 'white', fontWeight: 600 }}>
                                    {sWinRate.toFixed(1)}%
                                  </Typography>
                                </Box>
                                <Box sx={{
                                  display: 'flex', height: 10, width: '100%', borderRadius: 2,
                                  overflow: 'hidden', bgcolor: '#0F1115', border: '1px solid rgba(255,255,255,0.05)',
                                }}>
                                  {sWinRate > 0 && (
                                    <Box sx={{
                                      width: `${sWinRate}%`, bgcolor: '#00FF94',
                                      boxShadow: '0 0 10px rgba(0,255,148,0.5)',
                                      transition: 'width 0.5s ease',
                                    }} />
                                  )}
                                  {(100 - sWinRate) > 0 && s.closed.length > 0 && (
                                    <Box sx={{
                                      width: `${100 - sWinRate}%`, bgcolor: '#EF4444',
                                      transition: 'width 0.5s ease',
                                    }} />
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.6 }}>
                                  <Typography sx={{ fontSize: '0.6rem', fontFamily: '"JetBrains Mono", monospace', color: '#00FF94' }}>
                                    {sWins} Wins
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.6rem', fontFamily: '"JetBrains Mono", monospace', color: '#EF4444' }}>
                                    {sLosses} Loss
                                  </Typography>
                                </Box>
                              </Box>
                              
                              {/* Bottom stats table */}
                              <Box sx={{
                                bgcolor: 'rgba(19,21,25,0.5)', borderRadius: 2, p: 1.5,
                                border: '1px solid rgba(255,255,255,0.05)', position: 'relative', zIndex: 1,
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 500 }}>Trades</Typography>
                                  <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 500, textAlign: 'center' }}>Status</Typography>
                                  <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 500, textAlign: 'right' }}>Avg P&L</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>
                                    {s.trades.length}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 8, height: 8, borderRadius: '50%',
                                      bgcolor: s.open.length > 0 ? '#FBBF24' : '#6B7280',
                                      boxShadow: s.open.length > 0 ? '0 0 6px rgba(251,191,36,0.5)' : 'none',
                                    }} />
                                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', color: 'white' }}>
                                      {s.open.length} Open
                                    </Typography>
                                  </Box>
                                  <Typography sx={{
                                    fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, fontSize: '0.8rem',
                                    color: sAvgPnl > 0 ? '#00FF94' : sAvgPnl < 0 ? '#EF4444' : 'white',
                                    textAlign: 'right',
                                  }}>
                                    {sAvgPnl >= 0 ? '+' : ''}{formatCurrency(sAvgPnl)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    
                    {/* Footer */}
                    <Box sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)',
                    }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
                        Showing {keys.length} strategies — sorted by {strategySortMode === 'profitable' ? 'most profitable' : strategySortMode}
                      </Typography>
                    </Box>
                    </Collapse>
                  </Box>
                );
              })()}
              </>
            );
          })()}
          
          {/* Tab navigation — Glassmorphic Dark */}
          <Box sx={{
            mb: 4, borderRadius: 2, overflow: 'hidden',
            background: 'rgba(24,27,33,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                bgcolor: 'rgba(0,0,0,0.2)',
                '& .MuiTab-root': {
                  color: '#9CA3AF', fontWeight: 600, fontSize: '0.85rem', textTransform: 'none',
                  py: 1.5, transition: 'all 0.2s',
                  '&.Mui-selected': { color: '#00FF94' },
                  '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.03)' },
                },
                '& .MuiTabs-indicator': { bgcolor: '#00FF94', height: 2, boxShadow: '0 0 10px rgba(0,255,148,0.5)' },
              }}
            >
              <Tab label="Trade List" icon={<HistoryIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
              <Tab label="Performance" icon={<AssessmentIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
              <Tab label="Charts" icon={<TimelineIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
            </Tabs>
            
            {/* Tab content area */}
            <Box sx={{ p: 3 }}>
              {/* Trade List Tab */}
              {activeTab === 0 && (
                <Box>
                  {/* Toggle Bar: Open Only + Group by Strategy */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: groupByStrategy ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255,255,255,0.05)', 
                      borderRadius: 2, 
                      px: 2, 
                      py: 0.5,
                      border: '1px solid',
                      borderColor: groupByStrategy ? 'info.main' : 'grey.700'
                    }}>
                      <AccountTreeIcon sx={{ fontSize: 16, mr: 0.5, color: groupByStrategy ? 'info.main' : 'text.secondary' }} />
                      <Typography variant="body2" sx={{ mr: 1, color: groupByStrategy ? 'info.main' : 'text.secondary' }}>
                        Group by Strategy
                      </Typography>
                      <Switch
                        checked={groupByStrategy}
                        onChange={(e) => { setGroupByStrategy(e.target.checked); setActiveStrategyTab(0); }}
                        color="info"
                        size="small"
                      />
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: showOpenOnly ? 'rgba(46, 125, 50, 0.1)' : 'rgba(255,255,255,0.05)', 
                      borderRadius: 2, 
                      px: 2, 
                      py: 0.5,
                      border: '1px solid',
                      borderColor: showOpenOnly ? 'success.main' : 'grey.700'
                    }}>
                      <Typography variant="body2" sx={{ mr: 1, color: showOpenOnly ? 'success.main' : 'text.secondary' }}>
                        Show Open Only
                      </Typography>
                      <Switch
                        checked={showOpenOnly}
                        onChange={(e) => setShowOpenOnly(e.target.checked)}
                        color="success"
                        size="small"
                      />
                    </Box>
                    
                    {/* Global TP/SL Toggle for ALL open trades */}
                    {tradeData?.trades?.some(t => t.status === 'open' || t.status === 'pending') && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: 'rgba(156, 39, 176, 0.08)', 
                        borderRadius: 2, 
                        px: 1.5, 
                        py: 0.5,
                        border: '1px solid',
                        borderColor: 'rgba(156, 39, 176, 0.4)',
                        gap: 1.5,
                        opacity: bulkUpdating ? 0.6 : 1
                      }}>
                        <SmartToyIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                        <Tooltip title="Toggle Take Profit for ALL open positions">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Typography variant="caption" sx={{ color: globalTpEnabled ? 'success.main' : 'text.disabled', fontWeight: 'bold', fontSize: '0.7rem' }}>TP</Typography>
                            <Switch
                              checked={globalTpEnabled}
                              onChange={(e) => handleBulkTpSlToggle('autoTpEnabled', e.target.checked)}
                              color="success"
                              size="small"
                              disabled={bulkUpdating}
                              sx={{ 
                                '& .MuiSwitch-switchBase': { p: '3px' },
                                '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                width: 38, height: 22
                              }}
                            />
                          </Box>
                        </Tooltip>
                        <Tooltip title="Toggle Stop Loss for ALL open positions">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Typography variant="caption" sx={{ color: globalSlEnabled ? 'error.main' : 'text.disabled', fontWeight: 'bold', fontSize: '0.7rem' }}>SL</Typography>
                            <Switch
                              checked={globalSlEnabled}
                              onChange={(e) => handleBulkTpSlToggle('autoSlEnabled', e.target.checked)}
                              color="error"
                              size="small"
                              disabled={bulkUpdating}
                              sx={{ 
                                '& .MuiSwitch-switchBase': { p: '3px' },
                                '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                width: 38, height: 22
                              }}
                            />
                          </Box>
                        </Tooltip>
                        <Tooltip title="Toggle AI Exit for ALL open positions">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Typography variant="caption" sx={{ color: globalAiExitEnabled ? 'info.main' : 'text.disabled', fontWeight: 'bold', fontSize: '0.7rem' }}>AI</Typography>
                            <Switch
                              checked={globalAiExitEnabled}
                              onChange={(e) => handleBulkTpSlToggle('autoAiExitEnabled', e.target.checked)}
                              color="info"
                              size="small"
                              disabled={bulkUpdating}
                              sx={{ 
                                '& .MuiSwitch-switchBase': { p: '3px' },
                                '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                width: 38, height: 22
                              }}
                            />
                          </Box>
                        </Tooltip>
                        <Tooltip title="Toggle ALL (TP + SL + AI) for ALL open positions">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Typography variant="caption" sx={{ color: (globalTpEnabled && globalSlEnabled && globalAiExitEnabled) ? 'secondary.main' : 'text.disabled', fontWeight: 'bold', fontSize: '0.7rem' }}>All</Typography>
                            <Switch
                              checked={globalTpEnabled && globalSlEnabled && globalAiExitEnabled}
                              onChange={async (e) => {
                                const val = e.target.checked;
                                setBulkUpdating(true);
                                try {
                                  // v3.9.18: Include strategyId so backend persists global toggles to schedule
                                  const openT = tradeData?.trades?.filter(t => t.status === 'open' || t.status === 'pending') || [];
                                  const sId = openT[0]?.strategyId || null;
                                  await StrategyEngineService.bulkUpdatePositionSettings({ autoTpEnabled: val, autoSlEnabled: val, autoAiExitEnabled: val, ...(sId && { strategyId: sId }) });
                                  setTradeData(prev => {
                                    if (!prev) return prev;
                                    return {
                                      ...prev,
                                      trades: prev.trades.map(t => {
                                        if (t.status === 'open' || t.status === 'pending') {
                                          return { ...t, autoTpEnabled: val, autoSlEnabled: val, autoAiExitEnabled: val };
                                        }
                                        return t;
                                      })
                                    };
                                  });
                                  setGlobalTpEnabled(val);
                                  setGlobalSlEnabled(val);
                                  setGlobalAiExitEnabled(val);
                                } catch (err) {
                                  console.error('Bulk All TP/SL/AI update failed:', err);
                                } finally {
                                  setBulkUpdating(false);
                                }
                              }}
                              color="secondary"
                              size="small"
                              disabled={bulkUpdating}
                              sx={{ 
                                '& .MuiSwitch-switchBase': { p: '3px' },
                                '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                width: 38, height: 22
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Strategy Sub-Tabs (when groupByStrategy is ON) */}
                  {groupByStrategy && (() => {
                    // Extract unique strategy names from all mode-filtered trades
                    const allModeTrades = tradeData.trades.filter(trade => {
                      const isPaperTrade = trade.paperTrading === true || trade.isSimulated === true || !trade.realMoney;
                      return tradingMode === 'paper' ? isPaperTrade : !isPaperTrade;
                    });
                    const strategyMap = {};
                    allModeTrades.forEach(t => {
                      const name = t.strategyName || t.strategySnapshot?.name || `Strategy ${t.strategyId?.slice(-6) || t.runId?.slice(-6) || 'Unknown'}`;
                      const key = t.strategyId || t.runId || name;
                      if (!strategyMap[key]) strategyMap[key] = { name, count: 0, openCount: 0, realizedPnl: 0 };
                      strategyMap[key].count++;
                      if (t.status === 'open' || t.status === 'pending') strategyMap[key].openCount++;
                      if (t.status === 'closed') strategyMap[key].realizedPnl += (parseFloat(t.pnl) || 0);
                    });
                    const strategyKeys = Object.keys(strategyMap);
                    if (strategyKeys.length === 0) return null;
                    
                    // Clamp activeStrategyTab to valid range
                    const clampedTab = Math.min(activeStrategyTab, strategyKeys.length);
                    
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Tabs
                          value={clampedTab}
                          onChange={(e, v) => setActiveStrategyTab(v)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            minHeight: 36,
                            '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.8rem' },
                            bgcolor: 'rgba(255,255,255,0.03)',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Tab 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                All
                                <Chip label={allModeTrades.length} size="small" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />
                              </Box>
                            } 
                          />
                          {strategyKeys.map((key, i) => {
                            const sPnl = strategyMap[key].realizedPnl;
                            const sPnlStr = sPnl >= 0 ? `+$${Math.abs(sPnl).toFixed(2)}` : `-$${Math.abs(sPnl).toFixed(2)}`;
                            return (
                            <Tab
                              key={key}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {strategyMap[key].name}
                                  <Chip 
                                    label={strategyMap[key].count} 
                                    size="small" 
                                    sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} 
                                  />
                                  {strategyMap[key].openCount > 0 && (
                                    <Chip 
                                      label={`${strategyMap[key].openCount} open`} 
                                      size="small" 
                                      color="success"
                                      sx={{ height: 18, fontSize: '0.65rem', ml: 0.25 }} 
                                    />
                                  )}
                                  <Chip 
                                    label={sPnlStr}
                                    size="small" 
                                    sx={{ 
                                      height: 18, 
                                      fontSize: '0.65rem', 
                                      ml: 0.25,
                                      bgcolor: sPnl > 0 ? 'rgba(46,125,50,0.2)' : sPnl < 0 ? 'rgba(211,47,47,0.2)' : 'rgba(255,255,255,0.1)',
                                      color: sPnl > 0 ? 'success.main' : sPnl < 0 ? 'error.main' : 'text.secondary',
                                      fontWeight: 'bold'
                                    }} 
                                  />
                                </Box>
                              }
                            />
                            );
                          })}
                        </Tabs>
                      </Box>
                    );
                  })()}
                  
                  <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date & Time</TableCell>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="center"># Trades</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Direction</TableCell>
                          <TableCell align="right">Size ($)</TableCell>
                          <TableCell align="right">Entry</TableCell>
                          <TableCell align="right">Exit/Current</TableCell>
                          <TableCell align="right">Fees</TableCell>
                          <TableCell align="right">Net P&L</TableCell>
                          <TableCell align="right" sx={{ '@media (max-width: 959.95px)': { position: 'sticky', right: 110, zIndex: 4, bgcolor: '#0B1120', boxShadow: '-4px 0 8px rgba(0,0,0,0.4)' } }}>P&L %</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center" sx={{ minWidth: 80 }}>TP/SL</TableCell>
                          <TableCell align="center" sx={{ '@media (max-width: 959.95px)': { position: 'sticky', right: 0, zIndex: 4, bgcolor: '#0B1120' } }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Mother/Child grouped rendering */}
                        {(() => {
                          // 1. Filter & sort trades
                          const filteredTrades = [...tradeData.trades]
                            .filter(trade => {
                              const isPaperTrade = trade.paperTrading === true || trade.isSimulated === true || !trade.realMoney;
                              const modeMatch = tradingMode === 'paper' ? isPaperTrade : !isPaperTrade;
                              if (showOpenOnly) {
                                const isOpen = trade.status === 'open' || trade.status === 'pending' || trade.status === 'signal';
                                if (!(modeMatch && isOpen)) return false;
                              } else if (!modeMatch) return false;
                              
                              // Strategy tab filter (when groupByStrategy is ON and not "All" tab)
                              if (groupByStrategy && activeStrategyTab > 0) {
                                const allModeFiltered = tradeData.trades.filter(t => {
                                  const ip = t.paperTrading === true || t.isSimulated === true || !t.realMoney;
                                  return tradingMode === 'paper' ? ip : !ip;
                                });
                                const sMap = {};
                                allModeFiltered.forEach(t => {
                                  const key = t.strategyId || t.runId || t.strategyName || 'Unknown';
                                  if (!sMap[key]) sMap[key] = key;
                                });
                                const sKeys = Object.keys(sMap);
                                const selectedKey = sKeys[activeStrategyTab - 1];
                                if (selectedKey) {
                                  const tradeKey = trade.strategyId || trade.runId || trade.strategyName || 'Unknown';
                                  if (tradeKey !== selectedKey) return false;
                                }
                              }
                              return true;
                            })
                            .sort((a, b) => {
                              const aPinned = pinnedTradeIds.includes(a.id);
                              const bPinned = pinnedTradeIds.includes(b.id);
                              if (aPinned && !bPinned) return -1;
                              if (!aPinned && bPinned) return 1;
                              // Sort: open/pending first, then signals, then closed
                              const statusOrder = { open: 0, pending: 1, signal: 2, closed: 3, expired: 4 };
                              const aOrder = statusOrder[a.status] ?? 5;
                              const bOrder = statusOrder[b.status] ?? 5;
                              if (aOrder !== bOrder) return aOrder - bOrder;
                              return new Date(b.date) - new Date(a.date);
                            });
                          
                          // 2. Group by tradeGroupId (or fallback merge key with 5-min proximity)
                          const groups = {};
                          filteredTrades.forEach(trade => {
                            // Use tradeGroupId if available, else generate merge key with time bucket
                            let key = trade.tradeGroupId;
                            if (!key) {
                              // Fallback: group by symbol+side+mode+broker with 5-min time bucket
                              const timeBucket = Math.floor(new Date(trade.date).getTime() / (5 * 60 * 1000));
                              key = `${trade.symbol}:${trade.direction}:${trade.tradingMode || 'spot'}:${trade.broker || 'kucoin'}:${timeBucket}`;
                            }
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(trade);
                          });
                          
                          // 3. Build ordered render list preserving sort order
                          const renderRows = [];
                          const processedIds = new Set();
                          
                          filteredTrades.forEach(trade => {
                            if (processedIds.has(trade.id)) return;
                            let key = trade.tradeGroupId;
                            if (!key) {
                              const timeBucket = Math.floor(new Date(trade.date).getTime() / (5 * 60 * 1000));
                              key = `${trade.symbol}:${trade.direction}:${trade.tradingMode || 'spot'}:${trade.broker || 'kucoin'}:${timeBucket}`;
                            }
                            const group = groups[key];
                            
                            if (group && group.length > 1) {
                              // Multi-child group: mother row + children
                              group.forEach(t => processedIds.add(t.id));
                              renderRows.push({ type: 'mother', groupId: key, children: group });
                              if (expandedGroups[key]) {
                                group.forEach(child => renderRows.push({ type: 'child', trade: child, groupId: key }));
                              }
                            } else {
                              // Solo position
                              processedIds.add(trade.id);
                              renderRows.push({ type: 'solo', trade });
                            }
                          });
                          
                          // 4. Render rows
                          return renderRows.map((row, idx) => {
                            // === MOTHER ROW (aggregated group header) ===
                            if (row.type === 'mother') {
                              const children = row.children;
                              const isExpanded = expandedGroups[row.groupId];
                              // Aggregation
                              const totalQty = children.reduce((s, c) => s + (parseFloat(c.size) || 0), 0);
                              const totalSizeUsd = children.reduce((s, c) => s + ((parseFloat(c.size) || 0) * (parseFloat(c.entryPrice) || 0)), 0);
                              const avgEntry = totalQty > 0 ? children.reduce((s, c) => s + (parseFloat(c.entryPrice) || 0) * (parseFloat(c.size) || 0), 0) / totalQty : 0;
                              const totalFees = children.reduce((s, c) => s + (parseFloat(c.totalFees) || 0), 0);
                              const totalPnl = children.reduce((s, c) => s + (parseFloat(c.pnl) || 0), 0);
                              const totalMargin = children.reduce((s, c) => s + ((parseFloat(c.size) || 0) * (parseFloat(c.entryPrice) || 0)), 0);
                              const pnlPct = totalMargin > 0 ? (totalPnl / totalMargin) * 100 : 0;
                              const hasOpen = children.some(c => c.status === 'open' || c.status === 'pending');
                              const allClosed = children.every(c => c.status === 'closed');
                              const motherStatus = allClosed ? 'closed' : hasOpen ? 'open' : 'mixed';
                              const latestPrice = children[0]?.exitPrice || children[0]?.currentPrice;
                              const earliest = children.reduce((min, c) => new Date(c.date) < new Date(min) ? c.date : min, children[0].date);
                              const firstChild = children[0];
                              
                              return (
                                <TableRow 
                                  key={`mother-${row.groupId}`}
                                  hover
                                  onClick={() => toggleGroup(row.groupId)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(25, 118, 210, 0.06)',
                                    borderLeft: '3px solid #3b82f6',
                                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' }
                                  }}
                                >
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {isExpanded ? <ExpandMoreIcon sx={{ fontSize: 18, color: 'primary.main' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                                      <Typography variant="body2">{new Date(earliest).toLocaleString()}</Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip size="small" label={firstChild.symbol} color="secondary" />
                                      <Chip 
                                        size="small" 
                                        icon={<AccountTreeIcon sx={{ fontSize: 12 }} />}
                                        label={`${children.length} positions`}
                                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'primary.main', color: 'white' }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip size="small" label={children.length} color="primary" sx={{ fontWeight: 'bold', minWidth: 28 }} />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip size="small" label={(firstChild.tradingMode || 'spot').toUpperCase()} sx={{ height: 18, fontSize: '0.65rem', bgcolor: firstChild.tradingMode === 'futures' ? 'warning.main' : firstChild.tradingMode === 'margin' ? 'info.main' : 'success.main', color: 'white' }} />
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={firstChild.direction.toUpperCase()} color={firstChild.direction === 'buy' || firstChild.direction === 'long' ? 'success' : 'error'} />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalSizeUsd.toFixed(2)}</TableCell>
                                  <TableCell align="right">{formatPrice(avgEntry)}</TableCell>
                                  <TableCell align="right">{formatPrice(latestPrice)}</TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: 'warning.main', fontSize: '0.85rem' }}>
                                      ${totalFees.toFixed(4)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: totalPnl > 0 ? 'success.main' : totalPnl < 0 ? 'error.main' : 'inherit', fontWeight: 'bold' }}>
                                    {formatCurrency(totalPnl)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: pnlPct > 0 ? 'success.main' : pnlPct < 0 ? 'error.main' : 'inherit', fontWeight: 'bold', '@media (max-width: 959.95px)': { position: 'sticky', right: 110, zIndex: 1, bgcolor: '#181B21', boxShadow: '-4px 0 8px rgba(0,0,0,0.3)' } }}>
                                    {formatPercent(pnlPct)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={motherStatus === 'mixed' ? 'PARTIAL' : motherStatus.toUpperCase()} color={motherStatus === 'open' ? 'info' : motherStatus === 'mixed' ? 'warning' : 'default'} sx={{ fontWeight: 'bold' }} />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ '@media (max-width: 959.95px)': { position: 'sticky', right: 0, zIndex: 1, bgcolor: '#181B21' } }}>
                                    <Tooltip title={isExpanded ? "Collapse children" : "Expand children"}>
                                      <IconButton size="small" color="primary">
                                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            
                            // === CHILD ROW (indented under mother) or SOLO ROW ===
                            const trade = row.trade;
                            const isChild = row.type === 'child';
                            const isSignal = trade.status === 'signal';
                            return (
                          <TableRow 
                            key={trade.id} 
                            hover
                            onClick={() => { setSelectedTrade(trade); setDetailsOpen(true); }}
                            sx={{
                              cursor: 'pointer',
                              ...(isSignal && {
                                bgcolor: 'rgba(255, 152, 0, 0.06)',
                                borderLeft: '3px solid #F97316',
                                animation: 'signalPulse 3s infinite',
                                '@keyframes signalPulse': {
                                  '0%': { bgcolor: 'rgba(255, 152, 0, 0.06)' },
                                  '50%': { bgcolor: 'rgba(255, 152, 0, 0.12)' },
                                  '100%': { bgcolor: 'rgba(255, 152, 0, 0.06)' },
                                },
                              }),
                              ...(pinnedTradeIds.includes(trade.id) && !isSignal && {
                                backgroundColor: 'rgba(255, 193, 7, 0.08)',
                                borderLeft: '3px solid #FBBF24',
                              }),
                              ...(isChild && {
                                bgcolor: 'rgba(255,255,255,0.02)',
                                borderLeft: '3px solid rgba(25, 118, 210, 0.3)',
                              })
                            }}
                          >
                            <TableCell>{new Date(trade.date).toLocaleString()}</TableCell>
                            <TableCell>
                              <Tooltip 
                                arrow
                                placement="right"
                                title={trade.strategySnapshot ? (
                                  <Box sx={{ p: 0.5, minWidth: 200 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5 }}>
                                      {trade.strategySnapshot.name || trade.strategyName || 'Strategy'}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px', fontSize: 11 }}>
                                      <Typography variant="caption" color="grey.400">Mode:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{(trade.strategySnapshot.tradingMode || 'spot').toUpperCase()} {trade.strategySnapshot.leverage > 1 ? `${trade.strategySnapshot.leverage}×` : ''}</Typography>
                                      {trade.strategySnapshot.leverage > 1 && <>
                                        <Typography variant="caption" color="grey.400">Entry Mode:</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{trade.strategySnapshot.leverageEntryMode || 'strict'}</Typography>
                                      </>}
                                      <Typography variant="caption" color="grey.400">Confidence:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>≥{trade.strategySnapshot.confidenceThreshold || 70}%</Typography>
                                      <Typography variant="caption" color="grey.400">SL / TP:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{trade.strategySnapshot.stopLossPercent}% / {trade.strategySnapshot.takeProfitPercent}%</Typography>
                                      <Typography variant="caption" color="grey.400">Trailing Stop:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{trade.strategySnapshot.useTrailingStop ? (trade.strategySnapshot.trailingStopAiMode ? 'AI' : 'ON') : 'OFF'}</Typography>
                                      <Typography variant="caption" color="grey.400">Trailing Benefit:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{trade.strategySnapshot.useTrailingBenefit ? (trade.strategySnapshot.trailingBenefitAiMode ? 'AI' : 'ON') : 'OFF'}</Typography>
                                      <Typography variant="caption" color="grey.400">Volume Control:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{trade.strategySnapshot.enableVolumeControl ? 'ON' : 'OFF'}</Typography>
                                      {trade.strategySnapshot.hvMode && <>
                                        <Typography variant="caption" color="grey.400">HV Mode:</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#F97316' }}>ON</Typography>
                                      </>}
                                      <Typography variant="caption" color="grey.400">Universe:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{(trade.strategySnapshot.symbolUniverse || 'major_crypto').replace('_', ' ')}</Typography>
                                      <Typography variant="caption" color="grey.400">Scan:</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{trade.strategySnapshot.scanInterval || '5m'}</Typography>
                                    </Box>
                                    {trade.confidence && (
                                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'grey.400' }}>
                                        Signal confidence: {formatConfidence(trade.confidence)}%
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (trade.strategyName || '')}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: trade.strategySnapshot ? 'help' : 'default' }}>
                                  <Chip 
                                    size="small" 
                                    label={trade.symbol}
                                    color={trade.market === 'Crypto' ? 'secondary' : 
                                           trade.market === 'Stocks' ? 'primary' : 
                                           'default'}
                                  />
                                  {trade.isAggregated && trade.tradeCount > 1 && (
                                    <Chip 
                                      size="small" 
                                      label={`${trade.tradeCount} trades`}
                                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'info.main', color: 'white' }}
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              {trade.isAggregated && trade.tradeCount > 1 ? (
                                <Chip size="small" label={trade.tradeCount} color="info" sx={{ fontWeight: 'bold', minWidth: 28 }} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">1</Typography>
                              )}
                            </TableCell>
                            {/* Trade Type: Mode + Order Type + Leverage */}
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip 
                                    size="small" 
                                    label={(trade.tradingMode || 'spot').toUpperCase()}
                                    sx={{ 
                                      height: 18, 
                                      fontSize: '0.65rem',
                                      bgcolor: trade.tradingMode === 'futures' ? 'warning.main' : 
                                               trade.tradingMode === 'margin' ? 'info.main' : 
                                               'success.main',
                                      color: 'white'
                                    }}
                                  />
                                  <Chip 
                                    size="small" 
                                    label={(trade.orderType || 'market').toUpperCase()}
                                    sx={{ 
                                      height: 18, 
                                      fontSize: '0.65rem',
                                      bgcolor: trade.orderType === 'limit' ? 'secondary.main' : 'grey.600',
                                      color: 'white'
                                    }}
                                  />
                                </Box>
                                {(trade.leverage || 1) > 1 && (
                                  <Chip 
                                    size="small" 
                                    label={`${trade.leverage}× LEV`}
                                    sx={{ 
                                      height: 16, 
                                      fontSize: '0.6rem',
                                      bgcolor: 'error.dark',
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={trade.direction.toUpperCase()}
                                color={trade.direction === 'buy' || trade.direction === 'long' ? 'success' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="right">${((trade.size || 0) * (trade.entryPrice || 0)).toFixed(2)}</TableCell>
                            <TableCell align="right">{formatPrice(trade.entryPrice)}</TableCell>
                            <TableCell align="right">
                              {isSignal ? (
                                <Tooltip title="Live price — simulated tracking">
                                  <Typography variant="body2" sx={{ color: 'warning.main', cursor: 'help', fontSize: '0.85rem' }}>
                                    {formatPrice(trade.currentPrice || trade.exitPrice)}
                                  </Typography>
                                </Tooltip>
                              ) : formatPrice(trade.exitPrice)}
                            </TableCell>
                            {/* Fees Column */}
                            <TableCell align="right">
                              <Tooltip title={
                                <Box>
                                  <Typography variant="caption" display="block">Entry: ${(trade.entryCommission || 0).toFixed(4)} ({trade.entryFeePercent || 0.1}%)</Typography>
                                  <Typography variant="caption" display="block">Exit: ${(trade.exitCommission || 0).toFixed(4)} ({trade.exitFeePercent || 0.1}%)</Typography>
                                  <Typography variant="caption" display="block">Type: {trade.feeType || 'taker'}</Typography>
                                  <Typography variant="caption" display="block">Broker: {trade.broker || 'kucoin'}</Typography>
                                </Box>
                              }>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'warning.main',
                                    cursor: 'help',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  ${(trade.totalFees || 0).toFixed(4)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: parseFloat(trade.pnl) > 0 ? 'success.main' : 
                                       parseFloat(trade.pnl) < 0 ? 'error.main' : 
                                       parseFloat(trade.pnlPercent) < 0 ? 'error.main' :
                                       parseFloat(trade.pnlPercent) > 0 ? 'success.main' : 'inherit'
                              }}
                            >
                              {isSignal ? (
                                <Tooltip title="Simulated P&L from signal detection price">
                                  <Box sx={{ cursor: 'help' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                      {formatCurrency(trade.pnl, trade)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'warning.main' }}>SIM</Typography>
                                  </Box>
                                </Tooltip>
                              ) : formatCurrency(trade.pnl, trade)}
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: parseFloat(trade.pnlPercent) > 0 ? 'success.main' : 
                                       parseFloat(trade.pnlPercent) < 0 ? 'error.main' : 'inherit',
                                '@media (max-width: 959.95px)': { position: 'sticky', right: 110, zIndex: 1, bgcolor: '#181B21', boxShadow: '-4px 0 8px rgba(0,0,0,0.3)' },
                              }}
                            >
                              {isSignal ? (
                                <Tooltip title="Simulated P&L % from signal detection price">
                                  <Box sx={{ cursor: 'help' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                      {formatPercent(trade.pnlPercent)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'warning.main' }}>SIM</Typography>
                                  </Box>
                                </Tooltip>
                              ) : formatPercent(trade.pnlPercent)}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={
                                  trade.status === 'signal' ? 'SIGNAL' :
                                  trade.status === 'pending' ? 'PENDING' :
                                  trade.status === 'open' ? 'OPEN' :
                                  trade.status === 'closed' ? 'CLOSED' :
                                  trade.status === 'cancelled' ? 'CANCELLED' :
                                  trade.status?.toUpperCase() || 'OPEN'
                                }
                                color={
                                  trade.status === 'signal' ? 'warning' :
                                  trade.status === 'pending' ? 'warning' :
                                  trade.status === 'open' ? 'info' :
                                  trade.status === 'closed' ? 'default' :
                                  trade.status === 'cancelled' ? 'error' :
                                  'info'
                                }
                                sx={{
                                  fontWeight: 'bold',
                                  ...(trade.status === 'signal' && {
                                    bgcolor: '#F97316',
                                    color: 'white',
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                      '0%': { opacity: 1 },
                                      '50%': { opacity: 0.6 },
                                      '100%': { opacity: 1 },
                                    }
                                  }),
                                  ...(trade.status === 'pending' && {
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                      '0%': { opacity: 1 },
                                      '50%': { opacity: 0.6 },
                                      '100%': { opacity: 1 },
                                    }
                                  })
                                }}
                              />
                              {trade.status === 'signal' && trade.confidence && (
                                <Typography variant="caption" display="block" sx={{ color: 'warning.main', fontSize: '0.65rem', mt: 0.3 }}>
                                  {formatConfidence(trade.confidence)}% conf
                                </Typography>
                              )}
                              {trade.status === 'signal' && trade.signalExpiresAt && (
                                <SignalCountdown expiresAt={trade.signalExpiresAt} />
                              )}
                              {trade.orderType === 'limit' && trade.status === 'pending' && (
                                <Typography variant="caption" display="block" sx={{ color: 'warning.main', fontSize: '0.65rem' }}>
                                  Limit @ {trade.limitPrice}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              {trade.status === 'signal' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Chip size="small" label={`TP ${trade.dynamicTakeProfit || '—'}%`} sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(46,125,50,0.2)', color: 'success.main' }} />
                                  <Chip size="small" label={`SL ${trade.dynamicStopLoss || '—'}%`} sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(211,47,47,0.2)', color: 'error.main' }} />
                                </Box>
                              ) : (trade.status === 'open' || trade.status === 'pending') && trade.isStrategyPosition ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                                  <Tooltip title={!globalTpEnabled ? '🔒 TP disabled by global policy' : trade.autoTpEnabled !== false ? 'TP ON — click to disable' : 'TP OFF — click to enable'}>
                                    <span>
                                    <Switch
                                      size="small"
                                      checked={trade.autoTpEnabled !== false}
                                      onChange={(e) => handleRowTpSlToggle(trade, 'autoTpEnabled', e.target.checked)}
                                      color="success"
                                      disabled={!globalTpEnabled}
                                      sx={{ 
                                        '& .MuiSwitch-switchBase': { p: '2px' },
                                        '& .MuiSwitch-thumb': { width: 10, height: 10 },
                                        '& .MuiSwitch-track': { borderRadius: 2 },
                                        width: 28, height: 16,
                                        ...(!globalTpEnabled && { opacity: 0.35 }),
                                      }}
                                    />
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={!globalSlEnabled ? '🔒 SL disabled by global policy' : trade.autoSlEnabled !== false ? 'SL ON — click to disable' : 'SL OFF — click to enable'}>
                                    <span>
                                    <Switch
                                      size="small"
                                      checked={trade.autoSlEnabled !== false}
                                      onChange={(e) => handleRowTpSlToggle(trade, 'autoSlEnabled', e.target.checked)}
                                      color="error"
                                      disabled={!globalSlEnabled}
                                      sx={{ 
                                        '& .MuiSwitch-switchBase': { p: '2px' },
                                        '& .MuiSwitch-thumb': { width: 10, height: 10 },
                                        '& .MuiSwitch-track': { borderRadius: 2 },
                                        width: 28, height: 16,
                                        ...(!globalSlEnabled && { opacity: 0.35 }),
                                      }}
                                    />
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={!globalAiExitEnabled ? '🔒 AI disabled by global policy' : trade.autoAiExitEnabled !== false ? 'AI ON — click to disable' : 'AI OFF — click to enable'}>
                                    <span>
                                    <Switch
                                      size="small"
                                      checked={trade.autoAiExitEnabled !== false}
                                      onChange={(e) => handleRowTpSlToggle(trade, 'autoAiExitEnabled', e.target.checked)}
                                      color="info"
                                      disabled={!globalAiExitEnabled}
                                      sx={{ 
                                        '& .MuiSwitch-switchBase': { p: '2px' },
                                        '& .MuiSwitch-thumb': { width: 10, height: 10 },
                                        '& .MuiSwitch-track': { borderRadius: 2 },
                                        width: 28, height: 16,
                                        ...(!globalAiExitEnabled && { opacity: 0.35 }),
                                      }}
                                    />
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ '@media (max-width: 959.95px)': { position: 'sticky', right: 0, zIndex: 1, bgcolor: '#181B21' } }}>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {/* Signal-specific actions: Execute + View Chart + Dismiss */}
                                {trade.status === 'signal' && (
                                  <>
                                    <Tooltip title="Execute Signal — Open position at live price">
                                      <IconButton 
                                        size="small" 
                                        color="success"
                                        disabled={executingSignal === trade.id}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!window.confirm(`Execute ${trade.direction?.toUpperCase()} ${trade.symbol} at live price?\n\nSignal price: ${trade.entryPrice}\nConfidence: ${formatConfidence(trade.confidence)}%\nTP: ${trade.dynamicTakeProfit}% / SL: ${trade.dynamicStopLoss}%`)) return;
                                          setExecutingSignal(trade.id);
                                          try {
                                            await StrategyEngineService.executeSignal(trade.id);
                                            fetchTradeHistory();
                                          } catch (err) {
                                            setError(err?.response?.data?.error || 'Failed to execute signal');
                                          } finally {
                                            setExecutingSignal(null);
                                          }
                                        }}
                                        sx={{
                                          bgcolor: 'rgba(46, 125, 50, 0.15)',
                                          '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.3)' }
                                        }}
                                      >
                                        {executingSignal === trade.id ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View Chart">
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const tvSymbol = getTradingViewSymbol(trade.symbol, trade.broker || 'KUCOIN');
                                          window.open(`https://www.tradingview.com/chart/?symbol=${tvSymbol}`, '_blank');
                                        }}
                                      >
                                        <ShowChartIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Dismiss Signal">
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await StrategyEngineService.dismissSignal(trade.id);
                                            fetchTradeHistory();
                                          } catch (err) {
                                            setError('Failed to dismiss signal');
                                          }
                                        }}
                                        sx={{ opacity: 0.7 }}
                                      >
                                        <NotInterestedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                {trade.status !== 'signal' && (
                                <>
                                <Tooltip title={pinnedTradeIds.includes(trade.id) ? "Unpin Trade" : `Pin Trade (${pinnedTradeIds.length}/5)`}>
                                  <IconButton 
                                    size="small"
                                    color={pinnedTradeIds.includes(trade.id) ? "warning" : "default"}
                                    onClick={() => togglePinTrade(trade.id)}
                                  >
                                    {pinnedTradeIds.includes(trade.id) ? (
                                      <PushPinIcon fontSize="small" />
                                    ) : (
                                      <PushPinOutlinedIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Details">
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      setSelectedTrade(trade);
                                      setDetailsOpen(true);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {/* Loss Report Icon - Only for closed losing trades */}
                                {trade.status === 'closed' && parseFloat(trade.pnl || trade.pnlPercent || 0) < 0 && (
                                  <Tooltip title="View AI Loss Report">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => {
                                        setSelectedTrade(trade);
                                        setLossReportOpen(true);
                                      }}
                                      sx={{
                                        bgcolor: 'rgba(211, 47, 47, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.2)' }
                                      }}
                                    >
                                      <DescriptionIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {trade.isStrategyPosition && trade.status === 'open' && (trade.manualAddsUsed || 0) < 4 && (
                                  <Tooltip title={`Add Budget L${(trade.manualAddsUsed || 0) + 1}/4 (${[15,20,20,25][trade.manualAddsUsed || 0]}%)`}>
                                    <IconButton 
                                      size="small" 
                                      color="secondary"
                                      disabled={addingBudget === trade.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Add Budget Level ${(trade.manualAddsUsed || 0) + 1}/4 (${[15,20,20,25][trade.manualAddsUsed || 0]}% of base) to ${trade.symbol}?`)) {
                                          handleManualBudgetAdd(trade);
                                        }
                                      }}
                                      sx={{
                                        bgcolor: 'rgba(156, 39, 176, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.2)' }
                                      }}
                                    >
                                      {addingBudget === trade.id ? <CircularProgress size={16} /> : <AddCircleOutlineIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {trade.isStrategyPosition && (trade.status === 'open' || trade.status === 'pending') && (
                                  <Tooltip title={trade.status === 'pending' ? "Cancel Order" : "Close Position"}>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={async () => {
                                        try {
                                          await StrategyEngineService.closePosition(trade.runId, trade.id);
                                          fetchTradeHistory(); // Refresh data
                                        } catch (e) {
                                          console.error("Error closing position:", e);
                                          setError(trade.status === 'pending' ? "Failed to cancel order" : "Failed to close position");
                                        }
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              
              {/* Performance Tab — Glassmorphic Dark */}
              {activeTab === 1 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        background: '#181B21', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, p: 3,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 4px 6px -1px rgba(0,0,0,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': { borderColor: 'rgba(0,255,148,0.2)', transform: 'translateY(-1px)' },
                      }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssessmentIcon sx={{ fontSize: 18, color: '#00FF94' }} /> Performance Metrics
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Sharpe Ratio</TableCell>
                                  <TableCell align="right">
                                    {tradeData.performance.metrics.sharpeRatio}
                                    <Chip 
                                      size="small"
                                      label={parseFloat(tradeData.performance.metrics.sharpeRatio) > 1 ? "Good" : "Poor"}
                                      color={parseFloat(tradeData.performance.metrics.sharpeRatio) > 1 ? "success" : "error"}
                                      sx={{ ml: 1 }}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Win Rate</TableCell>
                                  <TableCell align="right">{tradeData.performance.metrics.winRate}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Profit Factor</TableCell>
                                  <TableCell align="right">{tradeData.performance.metrics.profitFactor}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Max Drawdown</TableCell>
                                  <TableCell align="right">{tradeData.performance.metrics.maxDrawdown}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Average Win</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>
                                    {tradeData.performance.metrics.avgWin}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Average Loss</TableCell>
                                  <TableCell align="right" sx={{ color: 'error.main' }}>
                                    {tradeData.performance.metrics.avgLoss}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Expectancy</TableCell>
                                  <TableCell 
                                    align="right"
                                    sx={{ 
                                      color: parseFloat(tradeData.performance.metrics.expectancy) > 0 ? 'success.main' : 'error.main' 
                                    }}
                                  >
                                    {tradeData.performance.metrics.expectancy}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        background: '#181B21', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, p: 3,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 4px 6px -1px rgba(0,0,0,0.3)',
                        height: '100%', transition: 'all 0.3s ease',
                        '&:hover': { borderColor: 'rgba(0,255,148,0.2)', transform: 'translateY(-1px)' },
                      }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BarChartIcon sx={{ fontSize: 18, color: '#00FF94' }} /> Strategy Performance
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mb: 2 }}>
                            Performance data from active trading strategies.
                          </Typography>
                          {tradeData?.trades?.length > 0 ? (
                            <Box sx={{ height: 200 }}>
                              <Bar
                                data={{
                                  labels: ['Wins', 'Losses', 'Open'],
                                  datasets: [{
                                    label: 'Trade Count',
                                    data: [
                                      tradeData.trades.filter(t => parseFloat(t.pnl) > 0).length,
                                      tradeData.trades.filter(t => parseFloat(t.pnl) < 0).length,
                                      tradeData.trades.filter(t => t.status === 'open' || t.status === 'pending').length
                                    ],
                                    backgroundColor: ['rgba(76, 175, 80, 0.7)', 'rgba(244, 67, 54, 0.7)', 'rgba(33, 150, 243, 0.7)'],
                                    borderColor: ['#00FF94', '#EF4444', '#3b82f6'],
                                    borderWidth: 1
                                  }]
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: { display: false },
                                    title: { display: false }
                                  },
                                  scales: {
                                    y: { beginAtZero: true, ticks: { color: '#aaa' }, grid: { color: 'rgba(255,255,255,0.1)' } },
                                    x: { ticks: { color: '#aaa' }, grid: { display: false } }
                                  }
                                }}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" color="text.secondary">No trades yet</Typography>
                            </Box>
                          )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Charts Tab — Glassmorphic Dark */}
              {activeTab === 2 && (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShowChartIcon sx={{ fontSize: 18, color: '#00FF94' }} /> Cumulative Returns
                  </Typography>
                  <Box sx={{
                    p: 3, mb: 3, height: '300px', borderRadius: 2,
                    background: '#181B21', border: '1px solid rgba(255,255,255,0.05)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 4px 6px -1px rgba(0,0,0,0.3)',
                  }}>
                    {tradeData?.performance?.dailyData?.length > 0 ? (
                      <Line
                        data={{
                          labels: tradeData.performance.dailyData.map(d => d.date),
                          datasets: [
                            {
                              label: 'Cumulative Return %',
                              data: tradeData.performance.dailyData.map(d => parseFloat(d.cumulativeReturn)),
                              borderColor: '#00FF94',
                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                              fill: true,
                              tension: 0.3,
                              pointRadius: 4,
                              pointBackgroundColor: '#00FF94'
                            },
                            {
                              label: 'Daily Return %',
                              data: tradeData.performance.dailyData.map(d => parseFloat(d.dailyReturn)),
                              borderColor: '#3b82f6',
                              backgroundColor: 'transparent',
                              borderDash: [5, 5],
                              tension: 0.3,
                              pointRadius: 3,
                              pointBackgroundColor: '#3b82f6'
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { 
                              display: true, 
                              position: 'top',
                              labels: { color: '#aaa' }
                            },
                            title: { display: false }
                          },
                          scales: {
                            y: { 
                              ticks: { color: '#aaa', callback: (v) => v + '%' }, 
                              grid: { color: 'rgba(255,255,255,0.1)' }
                            },
                            x: { 
                              ticks: { color: '#aaa', maxRotation: 45 }, 
                              grid: { display: false } 
                            }
                          }
                        }}
                      />
                    ) : (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="text.secondary">No performance data yet. Complete some trades to see charts.</Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white', mb: 2, mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon sx={{ fontSize: 18, color: '#00FF94' }} /> Monthly Returns
                  </Typography>
                  <TableContainer sx={{ bgcolor: '#181B21', borderRadius: 2, border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Daily Return</TableCell>
                          <TableCell align="right">Cumulative Return</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tradeData.performance.dailyData.slice(0, 10).map((day) => (
                          <TableRow key={day.date}>
                            <TableCell>{day.date}</TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: parseFloat(day.dailyReturn) > 0 ? 'success.main' : 
                                      parseFloat(day.dailyReturn) < 0 ? 'error.main' : 'inherit'
                              }}
                            >
                              {parseFloat(day.dailyReturn) > 0 ? '+' : ''}{day.dailyReturn}%
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: parseFloat(day.cumulativeReturn) > 0 ? 'success.main' : 
                                      parseFloat(day.cumulativeReturn) < 0 ? 'error.main' : 'inherit'
                              }}
                            >
                              {parseFloat(day.cumulativeReturn) > 0 ? '+' : ''}{day.cumulativeReturn}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{
          p: 4, textAlign: 'center', borderRadius: 2,
          background: 'rgba(24,27,33,0.6)', backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <Typography variant="h6" gutterBottom>
            No Trade Data Available
          </Typography>
          <Typography variant="body1">
            Unable to retrieve trade history from strategy nodes.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={fetchTradeHistory}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Trade Details Modal */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#1a1a2e', color: 'white', minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 1 }} /> {selectedTrade?.symbol}
            </Typography>
            <Chip 
              size="small" 
              label={selectedTrade?.direction?.toUpperCase()}
              color={selectedTrade?.direction === 'buy' ? 'success' : 'error'}
            />
            <Chip 
              size="small" 
              label={selectedTrade?.status?.toUpperCase()}
              color={selectedTrade?.status === 'open' ? 'info' : selectedTrade?.status === 'signal' ? 'warning' : 'default'}
              sx={selectedTrade?.status === 'signal' ? { bgcolor: '#F97316', color: 'white', fontWeight: 'bold' } : {}}
            />
            {selectedTrade?.status === 'signal' && (
              <Chip size="small" label="SIMULATION" sx={{ bgcolor: 'rgba(255,152,0,0.2)', color: '#F97316', fontWeight: 'bold', border: '1px solid #F97316', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.6 } } }} />
            )}
          </Box>
          <IconButton onClick={() => setDetailsOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedTrade && (
            <Box>
              {/* Large TradingView Chart with Price Level Overlay */}
              <Box sx={{ height: 400, bgcolor: '#131722', position: 'relative' }}>
                <iframe
                  src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_modal&symbol=${getTradingViewSymbol(selectedTrade.symbol, selectedTrade.broker || 'KUCOIN')}&interval=60&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=1a1a2e&studies=RSI@tv-basicstudies,MACD@tv-basicstudies&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=0&studies_overrides={}&overrides={}&enabled_features=[]&disabled_features=[]&locale=en`}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={`${selectedTrade.symbol} Chart`}
                  allowFullScreen
                />
                {/* Price Level Indicators - Right Side Overlay */}
                <Box sx={{ 
                  position: 'absolute', 
                  right: 8, 
                  top: 60, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1,
                  zIndex: 10,
                  pointerEvents: 'none'
                }}>
                  {/* Entry Price */}
                  <Box sx={{ 
                    bgcolor: 'rgba(33, 150, 243, 0.9)', 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#3b82f6', borderRadius: '50%' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                      Entry: ${selectedTrade.entryPrice?.toFixed(selectedTrade.entryPrice < 0.1 ? 6 : selectedTrade.entryPrice < 1 ? 4 : 2)}
                    </Typography>
                  </Box>
                  
                  {/* Take Profit - For BUY: above entry, For SELL: below entry */}
                  {selectedTrade.dynamicTakeProfit !== undefined && selectedTrade.dynamicTakeProfit !== null && (
                    (() => {
                      // Use absolute value for TP calculation (TP should always be positive distance from entry)
                      const tpPercent = Math.abs(parseFloat(selectedTrade.dynamicTakeProfit));
                      const isBuy = selectedTrade.direction === 'buy' || selectedTrade.direction === 'long';
                      const tpPrice = isBuy 
                        ? selectedTrade.entryPrice * (1 + tpPercent / 100)
                        : selectedTrade.entryPrice * (1 - tpPercent / 100);
                      return (
                        <Box sx={{ 
                          bgcolor: 'rgba(76, 175, 80, 0.9)', 
                          px: 1.5, 
                          py: 0.5, 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                        }}>
                          <Box sx={{ width: 8, height: 8, bgcolor: '#00FF94', borderRadius: '50%' }} />
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                            TP: ${tpPrice.toFixed(selectedTrade.entryPrice < 0.1 ? 6 : selectedTrade.entryPrice < 1 ? 4 : 2)} (+{tpPercent.toFixed(1)}%)
                          </Typography>
                        </Box>
                      );
                    })()
                  )}
                  
                  {/* Stop Loss - For BUY: below entry, For SELL: above entry */}
                  {selectedTrade.dynamicStopLoss !== undefined && selectedTrade.dynamicStopLoss !== null && (
                    (() => {
                      // Use absolute value for SL calculation (SL should always be positive distance from entry)
                      const slPercent = Math.abs(parseFloat(selectedTrade.dynamicStopLoss));
                      const isBuy = selectedTrade.direction === 'buy' || selectedTrade.direction === 'long';
                      const slPrice = isBuy 
                        ? selectedTrade.entryPrice * (1 - slPercent / 100)
                        : selectedTrade.entryPrice * (1 + slPercent / 100);
                      return (
                        <Box sx={{ 
                          bgcolor: 'rgba(244, 67, 54, 0.9)', 
                          px: 1.5, 
                          py: 0.5, 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                        }}>
                          <Box sx={{ width: 8, height: 8, bgcolor: '#EF4444', borderRadius: '50%' }} />
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                            SL: ${slPrice.toFixed(selectedTrade.entryPrice < 0.1 ? 6 : selectedTrade.entryPrice < 1 ? 4 : 2)} (-{slPercent.toFixed(1)}%)
                          </Typography>
                        </Box>
                      );
                    })()
                  )}
                  
                  {/* Current Price */}
                  <Box sx={{ 
                    bgcolor: 'rgba(255, 152, 0, 0.9)', 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#F97316', borderRadius: '50%' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                      Current: ${(selectedTrade.exitPrice || selectedTrade.currentPrice)?.toFixed(selectedTrade.entryPrice < 0.1 ? 6 : selectedTrade.entryPrice < 1 ? 4 : 2) || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Trade Details */}
              <Box sx={{ p: 3 }}>
                {/* v4.4.3: Signal Info Banner */}
                {selectedTrade.status === 'signal' && (
                  <Box sx={{ 
                    mb: 3, p: 2, 
                    bgcolor: 'rgba(255, 152, 0, 0.1)', 
                    border: '1px solid rgba(255, 152, 0, 0.4)', 
                    borderRadius: 2 
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#F97316', display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ mr: 1 }} /> Signal Details
                      </Typography>
                      {selectedTrade.signalExpiresAt && (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">Time Remaining</Typography>
                          <SignalCountdown expiresAt={selectedTrade.signalExpiresAt} />
                        </Box>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Confidence</Typography>
                          <Typography variant="h6" sx={{ color: '#F97316', fontWeight: 'bold' }}>
                            {formatConfidence(selectedTrade.confidence)}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Method</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {(selectedTrade.method || 'MTF').toUpperCase()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Signal Price</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            ${(selectedTrade.signalAddedPrice || selectedTrade.entryPrice)?.toFixed(selectedTrade.entryPrice < 1 ? 4 : 2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Validity</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {selectedTrade.signalValidityMinutes || 30} min
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {/* Simulated P&L Banner */}
                    <Box sx={{ 
                      mt: 2, p: 1.5, 
                      bgcolor: parseFloat(selectedTrade.pnlPercent) >= 0 ? 'rgba(46, 125, 50, 0.15)' : 'rgba(211, 47, 47, 0.15)',
                      border: '1px solid',
                      borderColor: parseFloat(selectedTrade.pnlPercent) >= 0 ? 'success.main' : 'error.main',
                      borderRadius: 1,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Simulated P&L (from signal detection)</Typography>
                        <Typography variant="h6" sx={{ 
                          color: parseFloat(selectedTrade.pnlPercent) >= 0 ? 'success.main' : 'error.main', 
                          fontWeight: 'bold' 
                        }}>
                          {parseFloat(selectedTrade.pnlPercent) >= 0 ? '+' : ''}{parseFloat(selectedTrade.pnlPercent || 0).toFixed(3)}%
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">Current Price</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          ${(selectedTrade.currentPrice || selectedTrade.entryPrice)?.toFixed(selectedTrade.entryPrice < 1 ? 4 : 2)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                      This is a simulated P&L tracking from the signal detection moment. When you execute, P&L will be calculated from the real entry price.
                    </Typography>
                    {/* v4.7.0: Signal Reason / AI Summary */}
                    {selectedTrade.signalReason && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(33, 150, 243, 0.08)', border: '1px solid rgba(33, 150, 243, 0.3)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>AI Signal Reason</Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>{selectedTrade.signalReason}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
                
                <Grid container spacing={3}>
                  {/* Left Column - Trade Info */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                      <ShowChartIcon sx={{ mr: 1 }} /> {selectedTrade.status === 'signal' ? 'Signal Information' : 'Trade Information'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Strategy</Typography>
                        <Typography variant="body2">{selectedTrade.strategyName || selectedTrade.strategy || 'Strategy Builder'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Direction</Typography>
                        <Chip 
                          size="small" 
                          label={selectedTrade.direction === 'buy' ? 'LONG' : 'SHORT'}
                          color={selectedTrade.direction === 'buy' ? 'success' : 'error'}
                        />
                      </Box>
                      {selectedTrade.status !== 'signal' && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Size</Typography>
                        <Typography variant="body2">${((selectedTrade.size || 0) * (selectedTrade.entryPrice || 0)).toFixed(2)}</Typography>
                      </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">{selectedTrade.status === 'signal' ? 'Signal Price' : 'Entry Price'}</Typography>
                        <Typography variant="body2">${selectedTrade.entryPrice?.toFixed(selectedTrade.entryPrice < 1 ? 4 : 2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">{selectedTrade.status === 'signal' ? 'Live Price' : 'Current/Exit Price'}</Typography>
                        <Typography variant="body2" sx={selectedTrade.status === 'signal' ? { color: 'warning.main' } : {}}>
                          ${selectedTrade.exitPrice?.toFixed(selectedTrade.entryPrice < 1 ? 4 : 2) || selectedTrade.currentPrice?.toFixed(selectedTrade.entryPrice < 1 ? 4 : 2) || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">{selectedTrade.status === 'signal' ? 'Detected At' : 'Opened At'}</Typography>
                        <Typography variant="body2">{(selectedTrade.signalAddedAt || selectedTrade.timestamp) ? new Date(selectedTrade.signalAddedAt || selectedTrade.timestamp).toLocaleString() : 'N/A'}</Typography>
                      </Box>
                      {selectedTrade.status === 'signal' && (
                      <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Broker</Typography>
                        <Typography variant="body2">{(selectedTrade.broker || 'kucoin').toUpperCase()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Trading Mode</Typography>
                        <Typography variant="body2">{selectedTrade.tradingMode || 'spot'}{selectedTrade.leverage > 1 ? ` ${selectedTrade.leverage}×` : ''}</Typography>
                      </Box>
                      </>
                      )}
                    </Box>
                  </Grid>
                  
                  {/* Right Column - P&L and AI Info */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                      <AccountBalanceWalletIcon sx={{ mr: 1 }} /> Performance & AI
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: (selectedTrade.pnl || selectedTrade.pnlPercent) >= 0 ? 'rgba(46, 125, 50, 0.2)' : 'rgba(211, 47, 47, 0.2)', 
                        border: '1px solid',
                        borderColor: (selectedTrade.pnl || selectedTrade.pnlPercent) >= 0 ? 'success.main' : 'error.main',
                        borderRadius: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="caption" color="text.secondary">Profit & Loss</Typography>
                        <Typography variant="h5" sx={{ color: parseFloat(selectedTrade.pnlPercent) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                          {(() => {
                            // Calculate P&L from trade data if not available
                            let pnlValue = parseFloat(selectedTrade.pnl) || 0;
                            if (pnlValue === 0 && selectedTrade.entryPrice && selectedTrade.size) {
                              const entryPrice = parseFloat(selectedTrade.entryPrice) || 0;
                              const exitPrice = parseFloat(selectedTrade.exitPrice || selectedTrade.currentPrice) || entryPrice;
                              const size = parseFloat(selectedTrade.size) || 0;
                              const direction = selectedTrade.direction || 'buy';
                              if (direction === 'buy' || direction === 'long') {
                                pnlValue = (exitPrice - entryPrice) * size;
                              } else {
                                pnlValue = (entryPrice - exitPrice) * size;
                              }
                            }
                            // Format with appropriate precision
                            if (Math.abs(pnlValue) < 0.01 && Math.abs(pnlValue) > 0.000001) {
                              return pnlValue >= 0 ? `$${pnlValue.toFixed(6)}` : `-$${Math.abs(pnlValue).toFixed(6)}`;
                            }
                            return pnlValue >= 0 ? `$${pnlValue.toFixed(4)}` : `-$${Math.abs(pnlValue).toFixed(4)}`;
                          })()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: parseFloat(selectedTrade.pnlPercent) >= 0 ? 'success.main' : 'error.main' }}>
                          ({parseFloat(selectedTrade.pnlPercent)?.toFixed(2) || '0.00'}%)
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Duration</Typography>
                        <Typography variant="body2">{selectedTrade.duration || 'N/A'}</Typography>
                      </Box>
                      
                      {/* AI Pro Trader Section */}
                      <Box sx={{ p: 1.5, bgcolor: 'rgba(25, 118, 210, 0.1)', border: '1px solid', borderColor: 'info.main', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            <SmartToyIcon sx={{ fontSize: 18, mr: 0.5 }} /> AI Pro Trader
                          </Typography>
                          <Chip 
                            size="small" 
                            label={selectedTrade.autoTpEnabled !== false && selectedTrade.autoSlEnabled !== false ? "AUTO" : "MANUAL"} 
                            sx={{ bgcolor: selectedTrade.autoTpEnabled !== false && selectedTrade.autoSlEnabled !== false ? 'success.main' : 'warning.main', color: 'white', height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                          {/* Take Profit with Toggle */}
                          <Box sx={{ flex: 1, p: 1.5, bgcolor: 'rgba(46, 125, 50, 0.2)', borderRadius: 1, textAlign: 'center', ...(!globalTpEnabled && { opacity: 0.5 }) }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Take Profit{!globalTpEnabled ? ' 🔒' : ''}</Typography>
                              <Tooltip title={!globalTpEnabled ? '🔒 Disabled by global policy' : ''}>
                                <span>
                                <Switch
                                  size="small"
                                  checked={selectedTrade.autoTpEnabled !== false}
                                  disabled={!globalTpEnabled}
                                  onChange={async (e) => {
                                    const val = e.target.checked;
                                    try {
                                      await StrategyEngineService.updatePositionSettings(selectedTrade.runId, selectedTrade.id, { autoTpEnabled: val });
                                      setSelectedTrade(prev => ({ ...prev, autoTpEnabled: val }));
                                      setTradeData(prev => prev ? { ...prev, trades: prev.trades.map(t => t.id === selectedTrade.id ? { ...t, autoTpEnabled: val } : t) } : prev);
                                    } catch (err) { console.error('Failed to update TP setting:', err); }
                                  }}
                                  sx={{ 
                                    p: 0, 
                                    '& .MuiSwitch-switchBase': { p: '2px' },
                                    '& .MuiSwitch-thumb': { width: 12, height: 12 },
                                    '& .MuiSwitch-track': { borderRadius: 10 },
                                    width: 32, height: 18
                                  }}
                                />
                                </span>
                              </Tooltip>
                            </Box>
                            <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                              {Math.abs(parseFloat(selectedTrade.dynamicTakeProfit) || selectedTrade.strategySnapshot?.takeProfitPercent || 2.5).toFixed(1)}%
                            </Typography>
                            {/* Show exact TP price */}
                            <Typography variant="caption" sx={{ display: 'block', color: 'success.light', fontWeight: 'bold', mt: 0.5 }}>
                              ${(() => {
                                const tp = Math.abs(parseFloat(selectedTrade.dynamicTakeProfit) || selectedTrade.strategySnapshot?.takeProfitPercent || 2.5);
                                const entry = parseFloat(selectedTrade.entryPrice) || 0;
                                const decimals = entry < 0.1 ? 6 : entry < 1 ? 4 : 2;
                                // For BUY: TP is above entry, For SELL: TP is below entry
                                return (selectedTrade.direction === 'buy' || selectedTrade.direction === 'long')
                                  ? (entry * (1 + tp / 100)).toFixed(decimals)
                                  : (entry * (1 - tp / 100)).toFixed(decimals);
                              })()}
                            </Typography>
                          </Box>
                          {/* Stop Loss with Toggle */}
                          <Box sx={{ flex: 1, p: 1.5, bgcolor: 'rgba(211, 47, 47, 0.2)', borderRadius: 1, textAlign: 'center', ...(!globalSlEnabled && { opacity: 0.5 }) }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Stop Loss{!globalSlEnabled ? ' 🔒' : ''}</Typography>
                              <Tooltip title={!globalSlEnabled ? '🔒 Disabled by global policy' : ''}>
                                <span>
                                <Switch
                                  size="small"
                                  checked={selectedTrade.autoSlEnabled !== false}
                                  disabled={!globalSlEnabled}
                                  onChange={async (e) => {
                                    const val = e.target.checked;
                                    try {
                                      await StrategyEngineService.updatePositionSettings(selectedTrade.runId, selectedTrade.id, { autoSlEnabled: val });
                                      setSelectedTrade(prev => ({ ...prev, autoSlEnabled: val }));
                                      setTradeData(prev => prev ? { ...prev, trades: prev.trades.map(t => t.id === selectedTrade.id ? { ...t, autoSlEnabled: val } : t) } : prev);
                                    } catch (err) { console.error('Failed to update SL setting:', err); }
                                  }}
                                  sx={{ 
                                    p: 0, 
                                    '& .MuiSwitch-switchBase': { p: '2px' },
                                    '& .MuiSwitch-thumb': { width: 12, height: 12 },
                                    '& .MuiSwitch-track': { borderRadius: 10 },
                                    width: 32, height: 18
                                  }}
                                />
                                </span>
                              </Tooltip>
                            </Box>
                            <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                              {Math.abs(parseFloat(selectedTrade.dynamicStopLoss) || selectedTrade.strategySnapshot?.stopLossPercent || 2.0).toFixed(1)}%
                            </Typography>
                            {/* Show exact SL price */}
                            <Typography variant="caption" sx={{ display: 'block', color: 'error.light', fontWeight: 'bold', mt: 0.5 }}>
                              ${(() => {
                                const sl = Math.abs(parseFloat(selectedTrade.dynamicStopLoss) || selectedTrade.strategySnapshot?.stopLossPercent || 2.0);
                                const entry = parseFloat(selectedTrade.entryPrice) || 0;
                                const decimals = entry < 0.1 ? 6 : entry < 1 ? 4 : 2;
                                // For BUY: SL is below entry, For SELL: SL is above entry
                                return (selectedTrade.direction === 'buy' || selectedTrade.direction === 'long')
                                  ? (entry * (1 - sl / 100)).toFixed(decimals)
                                  : (entry * (1 + sl / 100)).toFixed(decimals);
                              })()}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {selectedTrade.lastAdjustment ? (
                          <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, mb: 1 }}>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'warning.main', fontWeight: 'bold' }}>
                              <EditNoteIcon sx={{ fontSize: 14, mr: 0.5 }} /> Last AI Adjustment:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {selectedTrade.lastAdjustment.adjustment}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                              {selectedTrade.lastAdjustment.timestamp ? new Date(selectedTrade.lastAdjustment.timestamp).toLocaleString() : 'Recently'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontStyle: 'italic', mb: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} /> Waiting for first AI analysis...
                          </Typography>
                        )}
                        
                        {/* Technical Indicators */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedTrade.lastRsi && (
                            <Chip 
                              size="small" 
                              label={`RSI: ${selectedTrade.lastRsi?.toFixed(1)}`}
                              sx={{ 
                                bgcolor: selectedTrade.lastRsi > 70 ? 'error.main' : selectedTrade.lastRsi < 30 ? 'success.main' : 'grey.600',
                                color: 'white'
                              }}
                            />
                          )}
                          {selectedTrade.lastMacdSignal && (
                            <Chip 
                              size="small" 
                              label={`MACD: ${selectedTrade.lastMacdSignal}`}
                              sx={{ 
                                bgcolor: selectedTrade.lastMacdSignal === 'bullish' ? 'success.main' : 'error.main',
                                color: 'white'
                              }}
                            />
                          )}
                          {selectedTrade.confidence && (
                            <Chip 
                              size="small" 
                              label={`Confidence: ${formatConfidence(selectedTrade.confidence)}%`}
                              color="secondary"
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Action Buttons */}
                {selectedTrade.status === 'open' && selectedTrade.isStrategyPosition && (
                  <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {(selectedTrade.manualAddsUsed || 0) < 4 && (
                      <Button 
                        variant="outlined" 
                        color="secondary"
                        startIcon={addingBudget === selectedTrade.id ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
                        disabled={addingBudget === selectedTrade.id}
                        onClick={() => {
                          const level = (selectedTrade.manualAddsUsed || 0) + 1;
                          const pct = [15,20,20,25][selectedTrade.manualAddsUsed || 0];
                          if (window.confirm(`Add Budget Level ${level}/4 (${pct}% of base) to ${selectedTrade.symbol}?`)) {
                            handleManualBudgetAdd(selectedTrade);
                          }
                        }}
                      >
                        Add Budget L{(selectedTrade.manualAddsUsed || 0) + 1}/4 ({[15,20,20,25][selectedTrade.manualAddsUsed || 0]}%)
                      </Button>
                    )}
                    <Button 
                      variant="contained" 
                      color="error"
                      onClick={async () => {
                        try {
                          await StrategyEngineService.closePosition(selectedTrade.runId, selectedTrade.id);
                          setDetailsOpen(false);
                          fetchTradeHistory();
                        } catch (e) {
                          console.error("Error closing position:", e);
                        }
                      }}
                    >
                      Close Position
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* AI Trade Report Modal */}
      <Dialog 
        open={lossReportOpen} 
        onClose={() => setLossReportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: selectedTrade?.pnlPercent >= 0 ? 'success.dark' : 'error.dark', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 1 }} />
            {selectedTrade?.pnlPercent >= 0 ? 'AI Win Analysis Report' : 'AI Loss Analysis Report'}
          </Box>
          <IconButton onClick={() => setLossReportOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedTrade && (
            <Box>
              {/* Trade Summary Header */}
              <Box sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: selectedTrade.pnlPercent >= 0 ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: selectedTrade.pnlPercent >= 0 ? 'success.main' : 'error.main'
              }}>
                <Typography variant="h6" sx={{ color: selectedTrade.pnlPercent >= 0 ? 'success.main' : 'error.main', mb: 1 }}>
                  {selectedTrade.symbol} - {selectedTrade.direction?.toUpperCase()} Position
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Entry Price</Typography>
                    <Typography variant="body1" fontWeight="bold">${selectedTrade.entryPrice}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Exit Price</Typography>
                    <Typography variant="body1" fontWeight="bold">${selectedTrade.exitPrice}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">P&L</Typography>
                    <Typography variant="body1" fontWeight="bold" color={selectedTrade.pnlPercent >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(selectedTrade.pnl, selectedTrade)} ({formatPercent(selectedTrade.pnlPercent)})
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedTrade.duration}</Typography>
                  </Grid>
                </Grid>
              </Box>
              
              {/* Report Content - supports both win and loss reports */}
              {(selectedTrade.lossReport || selectedTrade.winReport) ? (
                <Box>
                  {/* Get the active report */}
                  {(() => {
                    const report = selectedTrade.winReport || selectedTrade.lossReport;
                    const isWin = selectedTrade.pnlPercent >= 0;
                    return (
                      <>
                        {/* Summary */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            📋 Summary
                          </Typography>
                          <Typography variant="body2" sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 1.5, borderRadius: 1 }}>
                            {report.summary}
                          </Typography>
                        </Box>
                        
                        {/* Win-specific: What Went Right */}
                        {isWin && report.whatWentRight?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'success.main' }}>
                              ✅ What Went Right
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {report.whatWentRight.map((reason, idx) => (
                                <Typography key={idx} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ marginRight: 8 }}>•</span> {reason}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Win-specific: Key Success Factors */}
                        {isWin && report.keySuccessFactors?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'info.main' }}>
                              🎯 Key Success Factors
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {report.keySuccessFactors.map((factor, idx) => (
                                <Typography key={idx} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ marginRight: 8 }}>⭐</span> {factor}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Loss-specific: Why Lost */}
                        {!isWin && report.whyLost?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'error.main' }}>
                              ❌ Why This Trade Lost
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {report.whyLost.map((reason, idx) => (
                                <Typography key={idx} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ marginRight: 8 }}>•</span> {reason}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Loss-specific: Why Target Missed */}
                        {!isWin && report.whyTargetMissed?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'warning.main' }}>
                              🎯 Why Target Was Not Reached
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {report.whyTargetMissed.map((reason, idx) => (
                                <Typography key={idx} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ marginRight: 8 }}>•</span> {reason}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Market Conditions & Analysis */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, height: '100%' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                📊 Market Conditions
                              </Typography>
                              <Typography variant="body2">{report.marketConditions}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, height: '100%' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                {isWin ? '📈 Risk/Reward' : '⚠️ Risk Assessment'}
                              </Typography>
                              <Typography variant="body2">{report.riskAssessment || report.riskReward}</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        
                        {/* Entry & Exit Analysis */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1, height: '100%' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'info.main' }}>
                                📥 Entry Analysis
                              </Typography>
                              <Typography variant="body2">{report.entryAnalysis}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 1, height: '100%' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'secondary.main' }}>
                                📤 Exit Analysis
                              </Typography>
                              <Typography variant="body2">{report.exitAnalysis}</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        
                        {/* Lessons Learned / Takeaways */}
                        {(report.lessonsLearned?.length > 0 || report.keyTakeaways?.length > 0) && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'info.main' }}>
                              {isWin ? '🏆 Key Takeaways' : '📚 Lessons Learned'}
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {(report.keyTakeaways || report.lessonsLearned).map((lesson, idx) => (
                                <Typography key={idx} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ marginRight: 8 }}>💡</span> {lesson}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Improvement Suggestions / Replication Tips */}
                        {(report.improvementSuggestions?.length > 0 || report.replicationTips?.length > 0) && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: isWin ? 'rgba(46, 125, 50, 0.1)' : 'rgba(46, 125, 50, 0.1)', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'success.main' }}>
                              {isWin ? '🔄 How to Replicate This Success' : '✅ Improvement Suggestions'}
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {(report.replicationTips || report.improvementSuggestions).map((suggestion, idx) => (
                                <Typography key={idx} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ marginRight: 8 }}>→</span> {suggestion}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 2 }}>
                          Report generated: {new Date(report.generatedAt).toLocaleString()}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No AI report available for this trade.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Click the button below to generate a detailed analysis report.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DescriptionIcon />}
                    onClick={async () => {
                      try {
                        const response = await fetch(
                          `${process.env.REACT_APP_API_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev'}/api/runs/${selectedTrade.runId}/positions/${selectedTrade.id}/generate-report`,
                          { method: 'POST' }
                        );
                        const result = await response.json();
                        if (result.success) {
                          // Update the selected trade with the new report - no page refresh needed
                          setSelectedTrade(prev => ({
                            ...prev,
                            ...result.data,
                            lossReport: result.data.lossReport,
                            winReport: result.data.winReport
                          }));
                          // Also update in the trade data list
                          setTradeData(prevData => prevData ? {
                            ...prevData,
                            trades: prevData.trades.map(t => 
                              t.id === selectedTrade.id ? { ...t, ...result.data } : t
                            )
                          } : prevData);
                        } else {
                          alert(result.error || 'Failed to generate report');
                        }
                      } catch (error) {
                        console.error('Error generating report:', error);
                        alert('Failed to generate report');
                      }
                    }}
                    sx={{ mt: 1 }}
                  >
                    Generate Trade Report
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TradeHistory;
